import AlertCircle from '@mdi/svg/svg/alert-circle.svg?react';
import CloudUpload from '@mdi/svg/svg/cloud-upload.svg?react';
import Download from '@mdi/svg/svg/download.svg?react';
import Launch from '@mdi/svg/svg/launch.svg?react';
import Pencil from '@mdi/svg/svg/pencil.svg?react';
import Send from '@mdi/svg/svg/send-variant-outline.svg?react';
import { type VmsApi as VmsApiType } from '@solink/vd-data-layer/api-clients/vms/vms';
import { type CallhomeCamera, type DeviceId, type SiteId } from '@solinkcorp/api-types';
import {
  DEFAULT_DOWNLOAD_TYPE,
  type UserPreferencesWithDefaults,
  type UserSession,
  useUserSession,
} from '@solinkcorp/authentication';
import { type LocaleDefinition } from '@solinkcorp/locale';
import {
  Flex,
  KeyActions,
  LoadingSpinner,
  MDIIcon,
  Modal,
  type ModalProps,
  toast,
  useWindowSize,
} from '@solinkcorp/solink-components';
import { getAccessibleAddress } from '@solinkcorp/utils';
import { EVENT_SUBTYPE_DOWNLOAD, EVENT_TYPE_BOOKMARK } from '@solinkcorp/vd-common';
import { type AuthLocation } from '@solinkcorp/vd-common/lib/esm/auth/authUtils';
import EventActions from 'actions/EventActions';
import axios from 'axios';
import classNames from 'classnames';
import { CloudVmsApi, PlatformApi, VmsApi } from 'clients';
import { type EventCamera, type IEvent, type Share } from 'clients/PlatformApi';
import { CommonContext, VideoContext } from 'components/AuthenticatedApp/AuthenticatedApp';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import CenteredLoadingSpinner from 'loadingIndicator/CenteredLoadingSpinner';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import concat from 'lodash/concat';
import debounce from 'lodash/debounce';
import difference from 'lodash/difference';
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import keys from 'lodash/keys';
import map from 'lodash/map';
import pickBy from 'lodash/pickBy';
import set from 'lodash/set';
import uniqBy from 'lodash/uniqBy';
import AnalyticsLogger from 'models/AnalyticsLogger';
import AuthService from 'models/AuthService';
import moment from 'moment';
import PQueue from 'p-queue';
import React, { Component, useContext, useMemo } from 'react';
import * as KioskUtils from 'src/core/utils/kioskUtils';
import { IS_DEV } from 'src/environment';
import useCamerasFromSites from 'src/hooks/camera/useCamerasFromSites';
import useDevices from 'src/hooks/devices/useDevices';
import useSites from 'src/hooks/sites/useSites';
import { useDevicesAreLocal } from 'src/hooks/useDeviceIsLocal';
import useLocale from 'src/hooks/useLocale';
import PollCloudStore from 'stores/PollCloudStore';
import PollConnectStore from 'stores/PollConnectStore';
import * as CameraUtil from 'utils/CameraUtil';
import CommonUtil from 'utils/CommonUtil';
import * as DateHandler from 'utils/DateHandler';
import * as EventUtil from 'utils/EventUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import Keyboard from 'utils/Keyboard';
import LocaleUtil from 'utils/LocaleUtil';
import { type VideoDiscoverySite } from 'utils/types';
import {
  AUTO_DOWNLOAD_TIMEOUT_MS,
  CLOUD_STATUS_COMPLETE,
  CLOUD_STATUS_ERROR,
  CLOUD_STATUS_IN_PROGRESS,
  CLOUD_STATUS_VERIFY,
  CONNECT_DOWNLOAD_STATUS_CANCELLED,
  CONNECT_DOWNLOAD_STATUS_DOWNLOADED,
  CONNECT_DOWNLOAD_STATUS_ENQUEUED,
  CONNECT_DOWNLOAD_STATUS_EXPIRED,
  CONNECT_DOWNLOAD_STATUS_FAILED,
  CONNECT_DOWNLOAD_STATUS_PROCESSING,
  CONNECT_DOWNLOAD_STATUS_READY,
  type ConnectDownloadStatus,
  CONTEXT_DOWNLOAD,
  CONTEXT_SAVETOCLOUD,
  DEFAULT_TIMEZONE,
  type EventContextType,
  KEYS,
  MAX_EVENT_DESCRIPTION_LENGTH,
  MAX_LENGTH_SHARE_MESSAGE,
  MODAL_CONTENT_TYPE,
  type PushToCloudStatus,
  SHARE_EXPIRATION_DAYS,
  SHARE_STATUS_DISABLED,
  THUMBNAIL_TYPE_CAMERA,
  THUMBNAIL_TYPE_EVENT,
  THUMBNAIL_TYPE_SNAPSHOT,
  type ThumbnailType,
  TWENTY_FOUR_HOURS_MS,
  VIDEO_DOWNLOAD_TYPES,
  VIDEO_SOURCE_CLOUD,
  VIDEO_SOURCE_LOCAL,
  VISIBILITY_PUBLIC,
  type VISIBILITY_TYPE,
} from 'vd-constants';
import PollProgress from '../PollProgress/PollProgress';
import {
  type LoadedSegments,
  type PlaybackController,
} from '../VideoPlayer/controller/PlaybackController';
import DetailsForm from './components/DetailsForm';
import DownloadDetails, { type DownloadStream } from './components/DownloadDetails';
import SavedDetails from './components/SavedDetails';
import SelectedCameras from './components/SelectedCameras';
import './SavePanel.sass';
import {
  type CamerasVideoSourceMap,
  type CameraVideoSource,
  type ModifiedIEvent,
  type ModifiedShare,
  type SavePanelCamera,
  type SavePanelCamerasById,
} from './types';
import useFaceBlurQuota, { type UseFaceBlurQuota } from './useFaceBlurQuota';
import * as SaveClipUtils from './utils/saveClipUtils';

const styles = {
  progressBar: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 5,
    height: 5,
    width: 'calc(100% - 100px)',
    marginRight: 7,
  },
  loadingIcon: {
    height: 14,
    width: 14,
  },
  dialogContent: {
    width: 892,
    maxWidth: 'none',
  },
  dialogTitle: {
    padding: '0 34px',
    height: 30,
    fontSize: 14,
  },
  dialogBody: {
    padding: 0,
    overflowY: 'visible',
  },
};

const MIN_DURATION_MS = 60 * 1000;
const INITIAL_ATTEMPT_DELAY = 500;
const MAX_ATTEMPT_DELAY = 30 * 1000;
const INITIAL_SEGMENTS_NUMBER = -1;

/*
  Regular Props
*/
interface SavePanelProps {
  activeEvent?: IEvent;
  closeSavePanel: () => void;
  editMode?: boolean;
  event: IEvent | undefined;
  isSaveTimelineEvent?: boolean;
  loadedSegments: LoadedSegments | undefined;
  playbackTime: number;
  playbackController?: PlaybackController;
  saveExistingEvent: boolean;
  saveMode: boolean;
  savePanelOpen: boolean;
  updateEvent?: (newEvent: IEvent) => void;
  playingCameras: PlayingCamera[];
  preferences: UserPreferencesWithDefaults;
  playerType?: string;
  onPushToCloud?: () => void;
}

/*
  Mapping of device ID to accessible address for multi-device sites
*/
export interface AccessibleAddresses {
  [deviceId: DeviceId]: string;
}

export function getAccessibleAddressForCamera(
  camera: { deviceId: DeviceId },
  accessibleAddresses: AccessibleAddresses,
): string | undefined {
  return accessibleAddresses[camera.deviceId];
}

export function getFirstAvailableAddress(
  accessibleAddresses: AccessibleAddresses,
): string | undefined {
  const addresses = Object.values(accessibleAddresses);
  return addresses.length > 0 ? addresses[0] : undefined;
}

/*
  Props made available in ComposeSavePanel
*/
interface SavePanelInnerProps extends SavePanelProps {
  alwaysLocal: boolean;
  locale: LocaleDefinition;
  playingCameras: PlayingCamera[];
  syncTimelineEvent: (event: IEvent) => void;
  user: UserSession;
  windowHeight: number;
  defaultVideoFormat: string;
  location: VideoDiscoverySite;
  areDevicesLocal: Record<string, boolean | undefined>;
  accessibleAddresses: AccessibleAddresses;
  faceblurQuota: UseFaceBlurQuota;
  cameras: CallhomeCamera[];
  siteCamerasById: Record<string, CallhomeCamera>;
  onPushToCloud?: () => void;
}

interface SavePanelState {
  cloudErrorNotification: boolean;
  confirmModalOpen: boolean;
  contextMode: EventContextType;
  description: string;
  diffShares: Partial<Share>[];
  displaySaveAsInProgress: boolean;
  downloadStreams: { [key: string]: DownloadStream };
  editMode: boolean;
  emails: string;
  endTime: Date | undefined;
  errorEmail: string;
  errorTime?: string;
  errorTitle: string;
  fillGaps: boolean;
  ifShare?: boolean;
  isLoading: boolean;
  isCameraSelectorOpen: boolean;
  longDuration: boolean;
  /** The original selected cameras of the existing clip or saved clip. */
  originalSelectedCameras: SavePanelCamerasById;
  originalEvent?: IEvent;
  pollingState?: PushToCloudStatus;
  readOnly: boolean;
  /** This saves the video playlist request abort controllers */
  saveExistingEvent: boolean;
  saveMode: boolean;
  selectedCameras: SavePanelCamerasById;
  selfShare: Partial<ModifiedShare>;
  selfSharePolling?: boolean;
  shareMessage: string;
  shares: Share[];
  shortDuration: boolean;
  shouldUseBestQuality: boolean;
  showCharCountDescription: boolean;
  showCharCountShareMessage: boolean;
  startTime: Date | undefined;
  /** The camera info of where video come from for this clip */
  camerasVideoSourceMap: CamerasVideoSourceMap;
  title?: string;
  uploadingPercentage: number | null;
  visibility: VISIBILITY_TYPE;
  saveMotionOnlyVideo: boolean;
  /** If video availability check is in progress*/
  isCheckingVideo: boolean;
  faceblur: boolean;
  availableCameras: SavePanelCamera[];
  debugMode: boolean;
}

class SavePanel extends Component<SavePanelInnerProps, SavePanelState> {
  private bodyContentRef = React.createRef<HTMLDivElement>();
  private downloadDebounce: () => void;
  private emailRegex: RegExp;
  private emailSplitOn: RegExp;
  private getShareLinkDelay: number;
  private isClipDurationRestricted: boolean;
  private keyboard: any;
  private location: VideoDiscoverySite;
  private locationId: SiteId | undefined;
  private locationName: string;
  private accessibleAddresses: AccessibleAddresses;
  private timezoneId: string;
  private _isMounted: boolean | undefined;
  private cameraSelectorRef = React.createRef<HTMLDivElement>();
  /** The abort controller of the current playlist requests.
   *  If new requests are made, we can use it to cancel the previous ones.
   */
  private requestAbortControllersByCameraId: { [key: string]: AbortController };
  private originalSelectedCameras: SavePanelCamerasById;
  private shortcuts: Array<{
    shortcut: string;
    action: () => void;
    isFormSafe: boolean;
    runWhenHelpShowing?: boolean;
    info: {
      title: string;
      group: string;
    };
  }>;

  constructor(props: SavePanelInnerProps) {
    super(props);

    const {
      playbackTime,
      loadedSegments,
      event,
      saveMode,
      saveExistingEvent,
      playingCameras,
      location,
      cameras,
      locale,
    } = props;

    // Initialize keyboard shortcuts
    this.shortcuts = IS_DEV
      ? [
          {
            shortcut: 'mod+shift+s',
            action: () => {
              this.setState({ debugMode: !this.state.debugMode });
            },
            isFormSafe: true,
            runWhenHelpShowing: true,
            info: {
              title: locale.general.showDebugInfo || 'Show Debug Info',
              group: 'Save Clips',
            },
          },
        ]
      : [];

    const havePlayingCamera = playingCameras.length > 0;

    const locationId = location.id;
    const { selectedCameras: originalSelectedCameras, availableCameras } = this.processCameras(
      cameras,
      event,
      playbackTime || Date.now(),
    );
    const contextMode = CONTEXT_SAVETOCLOUD;
    const displaySaveAsInProgress = !event
      ? false
      : event.pushToCloudStatus === CLOUD_STATUS_IN_PROGRESS;

    let longDuration = false;
    let startTime: Date | number | undefined;
    let endTime: Date | number | undefined;
    if (saveMode) {
      if (event) {
        const { startTime: eventStartTime, endTime: eventEndTime } = event;
        startTime = eventStartTime;
        endTime = eventEndTime;
        longDuration =
          new Date(endTime).getTime() - new Date(startTime).getTime() >= 60 * 60 * 1000;
        this.loadLatestEvent(event);
      } else if (playbackTime) {
        startTime = playbackTime;
        endTime = startTime;
      } else {
        startTime = endTime = Date.now();
      }
    } else if (event) {
      startTime = this.getEventStartTime(event);
      endTime = this.getEventEndTime(event);
      this.loadLatestEvent(event);
    }

    // prevent the timeline selector's end edge to exceed the camera recording time end
    if (!saveExistingEvent && havePlayingCamera) {
      let recordingEndTime;
      if (loadedSegments) {
        recordingEndTime = loadedSegments.segments[loadedSegments.segments.length - 1]?.end;
      }

      endTime =
        endTime && recordingEndTime && endTime.valueOf() > recordingEndTime
          ? new Date(recordingEndTime)
          : endTime;

      if (this.isShortDuration(startTime, endTime) && endTime) {
        startTime = DateHandler.subtractMinutesFromDate(1, endTime);
      }
    }

    this.locationId = locationId;
    this.locationName = location.name;
    this.location = location;
    this.timezoneId = location.timezone ?? DEFAULT_TIMEZONE;
    this.keyboard = new Keyboard();
    this.getShareLinkDelay = INITIAL_ATTEMPT_DELAY;
    this.isClipDurationRestricted = FeaturesUtil.isClipDurationRestricted();
    this.originalSelectedCameras = originalSelectedCameras;

    // Grabbed from https://emailregex.com/
    this.emailRegex = new RegExp(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
    this.emailSplitOn = /[\s,;]+/;

    /** Store the abort controller of the video playlist requests.
     * It can be used to cancel the previous requests if newer ones are fired.
     */
    this.requestAbortControllersByCameraId = {};

    /** The starting point of the best quality toggle.
     * We assume that user wants to use the best quality
     * unless later on we find out what video the cameras can offer
     * when the component is mounted.
     */
    let shouldUseBestQuality = true;
    if (event && EventUtil.isShared(event)) {
      shouldUseBestQuality = SaveClipUtils.isSharedClipBestQuality(event.cameras);
    }

    let selfShareState: Partial<ModifiedShare> = {};
    // Get shared spa link and url for downloads
    if (event) {
      const { id: eventId, shares, pushToCloudStatus } = event;

      if (
        contextMode === CONTEXT_SAVETOCLOUD &&
        !saveMode &&
        shares &&
        eventId &&
        (pushToCloudStatus === CLOUD_STATUS_COMPLETE || pushToCloudStatus === CLOUD_STATUS_VERIFY)
      ) {
        const selfShare = SaveClipUtils.getSelfShare(shares as Share[]);
        if (selfShare) selfShareState = selfShare;

        this._requestSharedViewLink(eventId, shares as Share[]);
      }
    }

    this.state = {
      camerasVideoSourceMap: {},
      cloudErrorNotification: false,
      confirmModalOpen: false,
      contextMode,
      description: '',
      diffShares: [],
      displaySaveAsInProgress,
      downloadStreams: {},
      editMode: false,
      emails: '',
      endTime: numberToDate(endTime),
      errorEmail: '',
      errorTime: '',
      errorTitle: '',
      fillGaps: event ? EventUtil.getFillGaps(event) : false,
      ifShare: false,
      isLoading: !saveMode,
      isCameraSelectorOpen: false,
      longDuration,
      originalSelectedCameras: cloneDeep(originalSelectedCameras),
      originalEvent: event ? cloneDeep(event) : undefined,
      pollingState: undefined,
      readOnly: false,
      saveExistingEvent: saveExistingEvent || !!event,
      saveMode,
      selectedCameras: cloneDeep(originalSelectedCameras),
      selfShare: selfShareState,
      selfSharePolling: undefined,
      shareMessage: '',
      shares: [],
      shortDuration: false,
      shouldUseBestQuality,
      showCharCountDescription: false,
      showCharCountShareMessage: false,
      startTime: numberToDate(startTime),
      title: saveExistingEvent ? event?.title : '',
      uploadingPercentage: null,
      visibility: SaveClipUtils.getClipDefaultVisibility(),
      saveMotionOnlyVideo: false,
      isCheckingVideo: false,
      faceblur: event ? EventUtil.getFaceblur(event) : false,
      availableCameras,
      debugMode: false,
    };

    this.downloadDebounce = debounce(this._handleDownloadUpdate, 2000);
  }

  componentDidMount() {
    const { startTime, endTime, shouldUseBestQuality, camerasVideoSourceMap, faceblur } =
      this.state;
    const { event, playbackTime, locale, user, saveMode, accessibleAddresses, cameras } =
      this.props;
    this._isMounted = true;

    const { selectedCameras } = this.processCameras(cameras, event, playbackTime || Date.now());
    const generateVideoCamerasShared = {
      accessibleAddresses,
      cameras: selectedCameras,
      cancelPreviousRequest: this.cancelPreviousPlaylistRequest,
      clipCamerasVideoSourceDict: camerasVideoSourceMap,
      isBestQuality: shouldUseBestQuality,
      locale,
      resetAbortController: this.resetPlaylistRequestAbortController,
      setNewRequestAbortController: this.setNewPlaylistRequestAbortController,
      updateIsCheckingVideo: this.updateIsCheckingVideo,
      user,
    };

    if (
      startTime &&
      endTime &&
      new Date(startTime).valueOf() < new Date(endTime).valueOf() &&
      (!event ||
        !EventUtil.isShared(event) ||
        (EventUtil.isShared(event) && event.pushToCloudStatus === CLOUD_STATUS_ERROR))
    ) {
      SaveClipUtils.generateNewCamerasVideoSource({
        ...generateVideoCamerasShared,
        startTime,
        endTime,
      }).then((camerasVideoSourceMap) => {
        this.updateCameraVideoSourceMapAndBestQuality(camerasVideoSourceMap, endTime);
      });
    } else if (event && EventUtil.isShared(event)) {
      const { shareStartTime, shareEndTime } = event;
      if (
        shareStartTime &&
        shareEndTime &&
        new Date(shareStartTime).valueOf() < new Date(shareEndTime).valueOf()
      ) {
        const eventCamerasDict: { [key: string]: EventCamera } = {};
        event.cameras.forEach((camera) => {
          eventCamerasDict[camera.id] = camera;
        });
        SaveClipUtils.generateSavedCamerasVideoSource({
          ...generateVideoCamerasShared,
          startTime: shareStartTime,
          endTime: shareEndTime,
          eventCamerasDict,
          shareStartTime,
          shareEndTime,
          pushToCloudStatus: event.pushToCloudStatus,
          faceblur,
        }).then((camerasVideoSourceMap) => {
          this.updateCameraVideoSourceMapAndBestQuality(camerasVideoSourceMap, endTime);
        });
      } else {
        throw new Error('shareStartTime or shareEndTime is not valid for this clip event.');
      }
    }

    const testAccessibleAddress = getFirstAvailableAddress(accessibleAddresses);
    if (saveMode && testAccessibleAddress) {
      /** Check if any device is accessible */
      VmsApi(testAccessibleAddress, false)
        .testAccess()
        .catch((error: any) => {
          console.warn(error);
          if (
            !axios.isAxiosError(error) ||
            axios.isCancel(error) ||
            (error as any)?.code === 'ECONNABORTED'
          )
            return;
          toast.warning(locale.camera.deviceOfflineMsg, {});
        });
    }

    PollCloudStore.addChangeListener(this.handlePollCloudChange);
    PollConnectStore.addChangeListener(this.handlePollConnectChange);
  }

  componentWillUnmount() {
    const { originalEvent } = this.state;
    const { siteCamerasById } = this.props;
    this._isMounted = false;
    PollCloudStore.removeChangeListener(this.handlePollCloudChange);
    PollConnectStore.removeChangeListener(this.handlePollConnectChange);

    if (originalEvent) {
      EventActions.connectPollStop(originalEvent, siteCamerasById);
      EventActions.cloudPollStop(originalEvent, siteCamerasById, 'cloud-panel');
    }
  }

  componentDidUpdate(prevProps: SavePanelInnerProps, prevState: SavePanelState) {
    const {
      camerasVideoSourceMap,
      contextMode,
      endTime,
      shouldUseBestQuality,
      pollingState,
      selectedCameras,
      selfShare,
      startTime,
      visibility,
      saveExistingEvent,
      saveMode,
      shares,
      originalEvent,
      faceblur,
    } = this.state;
    const { locale, user, accessibleAddresses, cameras, event, playbackTime } = this.props;
    const newStates: Partial<SavePanelState> = {};
    const streamsNeedUpdate =
      !moment(startTime).isSame(prevState.startTime) ||
      !moment(endTime).isSame(prevState.endTime) ||
      !this.areSameKeys(selectedCameras, prevState.selectedCameras) ||
      shouldUseBestQuality !== prevState.shouldUseBestQuality;
    const saveModeUpdate = saveMode !== prevState.saveMode;
    const eventUpdate = !isEqual(originalEvent, prevState.originalEvent);
    const gotShareLinkForNewClip =
      pollingState === CLOUD_STATUS_COMPLETE &&
      !get(prevState.selfShare, 'url') &&
      get(selfShare, 'url');
    const videoUploadedForEditClip =
      get(selfShare, 'url') &&
      prevState.pollingState === CLOUD_STATUS_IN_PROGRESS &&
      pollingState === CLOUD_STATUS_COMPLETE;
    const camerasChanged = !isEqual(cameras, prevProps.cameras);

    let newSelectedCameras = selectedCameras;
    if (camerasChanged) {
      const newProcessCamerasObj = this.processCameras(cameras, event, playbackTime || Date.now());
      newSelectedCameras = newProcessCamerasObj.selectedCameras;
      newStates.selectedCameras = cloneDeep(newSelectedCameras);
      newStates.availableCameras = cloneDeep(newProcessCamerasObj.availableCameras);
    }

    if (
      startTime &&
      endTime &&
      new Date(startTime).valueOf() < new Date(endTime).valueOf() &&
      (streamsNeedUpdate || saveModeUpdate || eventUpdate)
    ) {
      const generateVideoCameraShared = {
        accessibleAddresses,
        cameras: newSelectedCameras,
        cancelPreviousRequest: this.cancelPreviousPlaylistRequest,
        clipCamerasVideoSourceDict: camerasVideoSourceMap,
        endTime,
        isBestQuality: shouldUseBestQuality,
        locale,
        resetAbortController: this.resetPlaylistRequestAbortController,
        setNewRequestAbortController: this.setNewPlaylistRequestAbortController,
        startTime,
        updateIsCheckingVideo: this.updateIsCheckingVideo,
        user,
      };
      if (originalEvent && EventUtil.isShared(originalEvent)) {
        const eventCamerasDict: { [key: string]: EventCamera } = {};
        originalEvent.cameras.forEach((camera) => {
          eventCamerasDict[camera.id] = camera;
        });
        SaveClipUtils.generateSavedCamerasVideoSource({
          ...generateVideoCameraShared,
          eventCamerasDict,
          pushToCloudStatus: originalEvent.pushToCloudStatus,
          shareStartTime: originalEvent.shareStartTime,
          shareEndTime: originalEvent.shareEndTime,
          faceblur,
        }).then((camerasVideoSourceMap) => {
          this.updateCameraVideoSourceMapAndBestQuality(camerasVideoSourceMap, endTime);
        });
      } else {
        SaveClipUtils.generateNewCamerasVideoSource(generateVideoCameraShared).then(
          (camerasVideoSourceMap) => {
            this.updateCameraVideoSourceMapAndBestQuality(camerasVideoSourceMap, endTime);
          },
        );
      }
    }

    if (contextMode !== prevState.contextMode) {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      });
    }

    // Get shared link after the upload is done and the link exists
    if (
      pollingState !== prevState.pollingState &&
      pollingState === CLOUD_STATUS_COMPLETE &&
      !FeaturesUtil.hasNoFullAccessInKiosk() &&
      originalEvent
    ) {
      const untilSuccess = true;
      newStates.selfSharePolling = true;

      this._requestSharedViewLink(originalEvent.id, shares, untilSuccess);
    }

    if (gotShareLinkForNewClip || videoUploadedForEditClip) {
      this.handleSaveComplete();
    }

    if (!prevState.saveExistingEvent && saveExistingEvent && visibility !== VISIBILITY_PUBLIC) {
      newStates.visibility = VISIBILITY_PUBLIC;
    }

    if (!isEmpty(newStates)) {
      this.setState(newStates as SavePanelState);
    }
  }

  render() {
    const {
      camerasVideoSourceMap,
      confirmModalOpen,
      contextMode,
      description,
      diffShares,
      downloadStreams,
      emails,
      endTime,
      errorEmail,
      errorTime,
      errorTitle,
      fillGaps,
      longDuration,
      ifShare,
      shouldUseBestQuality,
      isCameraSelectorOpen,
      isLoading,
      saveMotionOnlyVideo,
      originalSelectedCameras,
      originalEvent,
      pollingState,
      saveExistingEvent,
      saveMode,
      selectedCameras,
      selfShare,
      selfSharePolling,
      shareMessage,
      shares,
      showCharCountShareMessage,
      startTime,
      title,
      visibility,
      isCheckingVideo,
      faceblur,
      availableCameras,
      debugMode,
    } = this.state;
    const {
      activeEvent,
      alwaysLocal,
      closeSavePanel,
      event,
      locale,
      savePanelOpen,
      user,
      windowHeight,
      areDevicesLocal,
      accessibleAddresses,
    } = this.props;

    const pushToCloudStatus = originalEvent?.pushToCloudStatus;
    const dialogBodyHeight = windowHeight * 0.8 - styles.dialogTitle.height - 64;
    const downloadStreamIds = this.getDownloadStreamIds();
    const userEmail = user.jwtUser.email;
    const userSharee = shares.find((share) => share.email === userEmail);
    const isBookmark = originalEvent && EventUtil.isBookmark(originalEvent);
    const isEditMode = this.isEditMode();
    const isCreator = selfShare.sharedBy === userEmail;
    const isAdmin = user.profile.userType === 'admin';
    const isSharee = userSharee && userSharee.status !== SHARE_STATUS_DISABLED;
    const isSolinkUser = user.isSolinkUser;
    // remove shares that have empty emails previously saved by bug
    const filteredShares = shares.filter((share) => share.email);

    const shouldRenderDownloadPanel =
      !isLoading && contextMode === CONTEXT_DOWNLOAD && !isEmpty(downloadStreams);
    const shouldRenderSavedDetails = contextMode === CONTEXT_SAVETOCLOUD && !saveMode;
    const cloudStatusError = pushToCloudStatus === CLOUD_STATUS_ERROR;
    const displayCloudStatus = saveMode && contextMode === CONTEXT_SAVETOCLOUD;
    const displayFillGaps =
      FeaturesUtil.isClipsFillGapsEnabled() && (isBookmark || !saveExistingEvent);
    const canCreatePrivate = FeaturesUtil.hasPrivateSharesEnabled();
    const canCreatePublic = FeaturesUtil.hasPublicSharesEnabled();
    const canCreate = canCreatePrivate || canCreatePublic;
    const canShare = canCreate && FeaturesUtil.canShareClips();
    const enableVisibilityEdit =
      canCreatePrivate &&
      canCreatePublic &&
      ((!isEditMode && !saveExistingEvent) ||
        (isEditMode && isBookmark && (isCreator || (isAdmin && isSharee) || isSolinkUser)));

    const cloudPanelClass = classNames('cloud-panel', {
      'save-mode': saveMode,
    });

    const isClipLongDuration = saveMode && longDuration;
    const isKiosk = KioskUtils.isKiosk();
    const isAnyVideoStreamHd = Object.values(camerasVideoSourceMap).find(
      (camera) => camera.streamName === 'HD',
    );
    const isLongDurationClipRestricted =
      this.isClipDurationRestricted && longDuration && (isAnyVideoStreamHd || isKiosk);
    let longDurationWarning = '';
    if (isLongDurationClipRestricted) {
      if (isKiosk) {
        longDurationWarning = locale.cloudShare.durationLongKiosk;
      } else {
        longDurationWarning = locale.cloudShare.durationLongHD;
      }
    } else if (isClipLongDuration) {
      longDurationWarning = locale.cloudShare.durationLong;
    }
    const areAnyCamerasMissingVideo = SaveClipUtils.hasNoVideoCameraForClip(camerasVideoSourceMap);
    const areAllCamerasMissingVideo = SaveClipUtils.noAvailableVideoForClip(camerasVideoSourceMap);
    const cameraHasNoVideoWarning = areAllCamerasMissingVideo
      ? locale.cloudShare.noCameraHasVideo
      : areAnyCamerasMissingVideo
        ? locale.cloudShare.anyCameraHasNoVideo
        : '';

    const isOnlySDVideoAvailable =
      endTime &&
      !!Object.keys(camerasVideoSourceMap).length &&
      SaveClipUtils.onlySDVideoAvailable(camerasVideoSourceMap, endTime);

    const blurDurationInMs = this.getFaceblurClipDuration();

    const hasExceededFaceblurLimit = this.hasExceededFaceblurLimit();

    const validationMessages = [
      {
        id: 'long-duration',
        textNode: longDurationWarning,
        isVisible: isClipLongDuration && saveMode,
        error: isLongDurationClipRestricted,
        warning: false,
      },
      {
        id: 'no-video',
        textNode: cameraHasNoVideoWarning,
        isVisible: areAnyCamerasMissingVideo && saveMode,
        error: areAllCamerasMissingVideo,
        warning: areAnyCamerasMissingVideo && !areAllCamerasMissingVideo,
      },
      {
        id: 'only-sd-video',
        textNode: locale.cloudShare.hdUnavaiableWarning,
        isVisible: isOnlySDVideoAvailable && saveMode,
        error: false,
        warning: true,
      },
      {
        id: 'faceblur-limit',
        textNode: (
          <>
            {locale.faceblur.faceBlurLimitExceeded}{' '}
            <a
              className="email-link"
              href={`mailto:support@solinkcorp.com?subject=${locale.faceblur.increaseFaceblurLimit}`}
            >
              {locale.faceblur.contactSupportIncreaseLimit}
            </a>
          </>
        ),
        isVisible: hasExceededFaceblurLimit && saveMode,
        error: true,
        warning: false,
      },
      {
        id: 'validate-face-blurring',
        textNode: locale.faceblur.validateFacesRecommendation,
        isVisible: faceblur && pushToCloudStatus === CLOUD_STATUS_VERIFY && !saveMode,
        error: false,
        warning: false,
      },
    ].filter((message) => message.isVisible);

    const shouldShowWarningBanner = validationMessages.length > 0;

    const selectedCamerasEditMode = saveMode && (!faceblur || !event);
    return (
      <KeyActions actions={this.shortcuts}>
        <Modal
          actions={this.getFooterActions()}
          bodyStyle={{ padding: 0 }}
          dataTestId="save-clip"
          className="cloud-panel-wrapper"
          dataIntercomTarget="cloud-panel-modal"
          footerChildren={this.getFooterChildren()}
          isOpen={savePanelOpen}
          onClose={closeSavePanel}
          style={{ gridTemplateRows: '0.5fr 80% 1fr' }}
          title={locale.cloudShare.title}
          contentType={MODAL_CONTENT_TYPE.SAVE_PANEL}
          type="extra-wide"
        >
          <div className={cloudPanelClass}>
            {isLoading && (
              <Flex justifyContent="center" alignItems="center" className="mask">
                <LoadingSpinner />
              </Flex>
            )}
            <div
              className={'body-content'}
              ref={this.bodyContentRef}
              style={{ height: dialogBodyHeight }}
            >
              <SelectedCameras
                availableCameras={availableCameras}
                canDownload={FeaturesUtil.canDownload()}
                canEdit={canCreate}
                cameras={selectedCameras}
                cameraSelectorRef={this.cameraSelectorRef}
                camerasVideoSourceMap={camerasVideoSourceMap}
                cloudStatus={pushToCloudStatus}
                contextMode={contextMode}
                debugMode={debugMode}
                event={originalEvent}
                generateProgressName={this.generateProgressName}
                getDeviceName={debugMode ? this.getDeviceName : undefined}
                isLoading={isLoading}
                isCameraSelectorOpen={isCameraSelectorOpen}
                isCheckingVideo={isCheckingVideo}
                locationName={this.locationName}
                saveInProgress={this.isInProgress()}
                saveMode={selectedCamerasEditMode}
                updateCameraSelectorOpen={this.updateCameraSelectorOpen}
                updateCamSelection={this.updateCamSelection}
                originalCameras={originalSelectedCameras}
                requestVmsPushForOneCam={this.requestVmsPushForOneCam}
                getPushToCloudStatusForOneCam={this.getPushToCloudStatusForOneCam}
                startTime={startTime}
                endTime={endTime}
              />
              <div className="content-scroll-wrapper">
                <Flex className="content-scroll active">
                  <div className="context-panel active">
                    {saveMode && !isLoading && (
                      <DetailsForm
                        activeEvent={activeEvent}
                        camerasVideoSourceMap={camerasVideoSourceMap}
                        selectedCameras={selectedCameras}
                        canCreate={canCreate}
                        canShare={canShare}
                        contextMode={contextMode}
                        description={description}
                        diffShares={diffShares}
                        displayFillGaps={displayFillGaps}
                        emails={emails}
                        enableVisibilityEdit={enableVisibilityEdit}
                        endTime={endTime}
                        errorEmail={errorEmail}
                        errorTime={errorTime}
                        errorTitle={errorTitle}
                        event={originalEvent}
                        fillGaps={fillGaps}
                        isCreator={isCreator}
                        ifShare={ifShare}
                        isBestQuality={shouldUseBestQuality}
                        isEditMode={isEditMode}
                        saveMotionOnlyVideo={saveMotionOnlyVideo}
                        onBlur={this.onBlur}
                        onChange={this.onChange}
                        onDateChange={this.onDateChange}
                        onFocusShareMessage={this.onFocusShareMessage}
                        onKeyPressEmail={this._onKeyPressEmail}
                        removeShare={this.removeShare}
                        saveExistingEvent={saveExistingEvent}
                        shareMessage={shareMessage}
                        shares={filteredShares}
                        showCharCountShareMessage={showCharCountShareMessage}
                        startTime={startTime}
                        title={title}
                        timezoneId={this.timezoneId}
                        updateDiffSharesState={this.updateDiffSharesState}
                        updateEmailsState={this.updateEmailsState}
                        updateShareMessageState={this.updateShareMessageState}
                        updateSharesState={this.updateSharesState}
                        updateSaveActiveEvent={this.updateSaveActiveEvent}
                        visibility={visibility}
                        faceblur={faceblur}
                        blurDurationInMs={blurDurationInMs}
                      />
                    )}
                    {shouldRenderSavedDetails && originalEvent && (
                      <SavedDetails
                        canShare={canShare}
                        cloudStatusError={cloudStatusError}
                        contextMode={contextMode}
                        displayCloudStatus={displayCloudStatus}
                        displayFillGaps={displayFillGaps}
                        enterEditMode={this.enterEditMode}
                        errorEmail={errorEmail}
                        event={originalEvent}
                        fillGaps={fillGaps}
                        ifShare={ifShare}
                        isBestQuality={shouldUseBestQuality}
                        isInProgress={this.isInProgress()}
                        isLoading={isLoading}
                        onChange={this.onChange}
                        pollingState={pollingState}
                        selfShare={selfShare}
                        selfSharePolling={selfSharePolling}
                        shareMessage={shareMessage}
                        shares={filteredShares}
                        timezoneId={this.timezoneId}
                        visibility={visibility}
                        tenantId={user.customerId}
                        faceblur={faceblur}
                      />
                    )}
                    {shouldRenderDownloadPanel && originalEvent && (
                      <>
                        <SavedDetails
                          canShare={false}
                          contextMode={contextMode}
                          event={originalEvent}
                          ifShare={false}
                          timezoneId={this.timezoneId}
                          tenantId={user.customerId}
                        />
                        <DownloadDetails
                          selectedCameras={selectedCameras}
                          downloadStreamIds={downloadStreamIds}
                          downloadStreams={downloadStreams}
                          event={originalEvent}
                          editMode={saveMode}
                          areDevicesLocal={areDevicesLocal}
                          alwaysLocal={alwaysLocal}
                          generateProgressName={this.generateProgressName}
                          onDownloadDetailRowButtonClick={this.onDownloadDetailRowButtonClick}
                          devices={this.location.devices}
                          accessibleAddresses={accessibleAddresses}
                        />
                      </>
                    )}
                    {validationMessages.length > 0 && (
                      <div
                        style={{
                          height: `calc(${validationMessages.length} * var(--validationBannerHeight))`,
                        }}
                      ></div>
                    )}
                  </div>
                </Flex>
              </div>
            </div>
          </div>
          {shouldShowWarningBanner && (
            <Flex className="warning-banner-wrapper" flexDirection="column" width="auto">
              {validationMessages.map(({ id, textNode, error, warning }) => (
                <Flex
                  className={classNames('warning-banner', { error, warning })}
                  alignItems="center"
                  key={id}
                >
                  <MDIIcon icon={AlertCircle} size={18} />
                  <div>{textNode}</div>
                </Flex>
              ))}
            </Flex>
          )}
        </Modal>
        <Modal
          actions={[
            {
              text: locale.general.cancel,
              isPrimary: false,
              onClick: this.closeDownloadConfirm,
            },
            {
              text: locale.general.continue,
              isPrimary: true,
              onClick: this.downloadInStore,
            },
          ]}
          className="download-confirm-wrapper"
          dataTestId="save-clip-confirm"
          isOpen={confirmModalOpen}
          onClose={this.closeDownloadConfirm}
          style={{ gridTemplateRows: '0.5fr auto 1fr' }}
          title={locale.download.download}
          contentType={MODAL_CONTENT_TYPE.SAVE_PANEL}
        >
          <div className="download-confirm-text">
            <p>{locale.download.downloadConfirmNote}</p>
            <p>{locale.download.downloadConfirm}</p>
          </div>
        </Modal>
      </KeyActions>
    );
  }

  getDeviceName = (deviceId: string) => {
    const device = this.props.location.devices?.find((d: any) => d.id === deviceId);
    return device?.name;
  };

  isEditMode = () =>
    (this.state.saveMode &&
      this.state.originalEvent &&
      EventUtil.isShared(this.state.originalEvent)) ??
    false;

  updateSaveActiveEvent = (value: boolean) => {
    const { activeEvent, playbackTime, cameras } = this.props;
    const newState: Partial<SavePanelState> = {
      saveExistingEvent: value,
    };
    if (value) {
      const { selectedCameras: newSelectedCameras } = this.processCameras(
        cameras,
        activeEvent,
        playbackTime || Date.now(),
      );
      newState.title = activeEvent?.title;
      newState.originalEvent = cloneDeep(activeEvent);
      newState.selectedCameras = cloneDeep(newSelectedCameras);
    } else {
      newState.title = '';
      newState.originalEvent = {} as IEvent;
      newState.selectedCameras = cloneDeep(this.originalSelectedCameras);
    }

    this.setState(newState as SavePanelState);
  };

  getEventStartTime = ({ shareStartTime, startTime }: IEvent) =>
    shareStartTime ? new Date(shareStartTime) : startTime;

  getEventEndTime = ({ shareEndTime, endTime }: IEvent) =>
    shareEndTime ? new Date(shareEndTime) : endTime;

  isInProgress = () => {
    const { displaySaveAsInProgress, originalEvent } = this.state;
    return (
      PollCloudStore.isInProgress(originalEvent) ||
      displaySaveAsInProgress ||
      PollConnectStore.isProcessing(originalEvent)
    );
  };

  getFooterChildren() {
    const { locale } = this.props;

    const {
      contextMode,
      displaySaveAsInProgress,
      originalEvent,
      uploadingPercentage,
      faceblur,
      saveMode,
    } = this.state;

    const hasExceededFaceblurLimit = this.hasExceededFaceblurLimit();
    const remainingFaceblurTime = this.getNewFaceblurQuota();
    const pollProgressVisible =
      originalEvent?.id && contextMode === CONTEXT_SAVETOCLOUD && displaySaveAsInProgress;
    const faceblurQuotaVisible = faceblur && saveMode && !pollProgressVisible;

    return (
      <Flex alignItems="center" className="footer-content" style={{ flex: 1 }}>
        {faceblurQuotaVisible && (
          <p className={classNames('faceblur-balance', { error: hasExceededFaceblurLimit })}>
            {LocaleUtil.getLocalizedStr({
              string: locale.faceblur.faceblurBalanceMins,
              replacements: { minutes: Math.round(remainingFaceblurTime / 1000 / 60) },
            })}
          </p>
        )}
        {pollProgressVisible && (
          <PollProgress
            barStyle={styles.progressBar}
            displayInstruction={true}
            locale={locale}
            percent={uploadingPercentage}
          />
        )}
      </Flex>
    );
  }

  getFooterActions() {
    const {
      camerasVideoSourceMap,
      contextMode,
      downloadStreams,
      errorTime,
      ifShare,
      isCameraSelectorOpen,
      isLoading,
      originalEvent,
      saveExistingEvent,
      saveMode,
      selfShare,
    } = this.state;
    const { alwaysLocal, closeSavePanel, locale } = this.props;
    const pushToCloudStatus = originalEvent?.pushToCloudStatus;
    const canShare = FeaturesUtil.canShareClips();
    const shouldDisplayDownloadButton = this.canCreateDownload();
    const noVideo = SaveClipUtils.noAvailableVideoForClip(camerasVideoSourceMap);

    const disableUpdate =
      !this._validateTitle() ||
      (ifShare && !this._validateEmail()) ||
      !!errorTime ||
      isCameraSelectorOpen ||
      noVideo;

    const cloudStatusError = pushToCloudStatus === CLOUD_STATUS_ERROR;
    const alreadyDownloaded = !isEmpty(downloadStreams);
    const iconStyle = { fontSize: 15, top: 1 };

    const shouldRenderDownloadPanel =
      !isLoading && contextMode === CONTEXT_DOWNLOAD && alreadyDownloaded;

    const disableViewShare =
      this.isInProgress() || cloudStatusError || isEmpty(selfShare) || !selfShare.url;

    const downloadButtonLabel = alwaysLocal
      ? locale.download.downloadOnly
      : locale.cloudShare.localDownload;

    const closeAction = {
      text: locale.general.close,
      isPrimary: false,
      onClick: closeSavePanel,
    };

    const retryAction = {
      icon: <MDIIcon icon={CloudUpload} size={15} />,
      onClick: this.retrySave,
      text: locale.general.retry,
    };

    const localDownloadAction = {
      text: downloadButtonLabel,
      disabled: this.shouldDisableDownload(),
      icon: <MDIIcon icon={Download} size={15} />,
      onClick: this.openDownloadConfirm,
      tooltip: locale.download.downloadTooltip,
    };

    const viewShareAction = {
      text: locale.cloudShare.viewShare,
      disabled: disableViewShare,
      icon: <MDIIcon icon={Launch} size={15} />,
      iconStyle,
      onClick: disableViewShare ? undefined : this.viewSharePage,
    };

    const editActions = [
      {
        text: locale.general.cancel,
        isPrimary: false,
        onClick: this.exitEditMode,
      },
      {
        text: locale.general.update,
        disabled: disableUpdate,
        icon: <MDIIcon icon={CloudUpload} size={15} />,
        onClick: this.saveClipChanges,
      },
    ];

    const saveNewActions: Required<ModalProps>['actions'] = [closeAction];
    const saveFailedActions: Required<ModalProps>['actions'] = [closeAction];
    const saveDoneActions: Required<ModalProps>['actions'] = [closeAction];

    if (this.isInProgress()) {
      saveDoneActions.push(retryAction);
    }

    if (!KioskUtils.isKiosk()) {
      if (shouldDisplayDownloadButton) {
        saveNewActions.push(localDownloadAction);
        saveFailedActions.push(localDownloadAction);
      }

      if (canShare) {
        saveDoneActions.push(viewShareAction);
      }
    }

    saveNewActions.push({
      text: locale.general.save,
      disabled: this.shouldDisableSubmit(),
      icon: <MDIIcon icon={CloudUpload} size={15} />,
      onClick: saveExistingEvent ? this.saveExistingEvent : this.saveNewClipToCloud,
    });

    saveFailedActions.push(retryAction);

    saveDoneActions.push({
      text: locale.general.edit,
      disabled: this.isInProgress() || isEmpty(selfShare),
      icon: <MDIIcon icon={Pencil} size={15} />,
      onClick: this.enterEditMode,
    });

    if (pushToCloudStatus === CLOUD_STATUS_VERIFY || this.isInProgress()) {
      saveDoneActions.push({
        text: locale.general.send,
        disabled: this.isInProgress() || isEmpty(selfShare),
        icon: <MDIIcon icon={Send} size={15} />,
        onClick: this.notifyEmailRecipients,
      });
    }

    if (saveMode) {
      return originalEvent && EventUtil.isShared(originalEvent) ? editActions : saveNewActions;
    } else if (!isLoading && !shouldRenderDownloadPanel) {
      return cloudStatusError ? saveFailedActions : saveDoneActions;
    } else if (alreadyDownloaded) {
      return [closeAction];
    }
  }

  retrySave = () => {
    const { originalEvent, camerasVideoSourceMap } = this.state;

    this.setState(
      {
        isLoading: true,
        contextMode: CONTEXT_SAVETOCLOUD,
      },
      () => {
        if (!originalEvent) {
          throw new Error('No originalEvent to retry save');
        }

        const { shareStartTime, shareEndTime } = originalEvent;
        if (!shareStartTime || !shareEndTime) {
          throw new Error('Missing shareStartTime or shareEndTime');
        }

        // if the new streams to save is different with the original event cameras
        // (possible when the previous save attempt failed),
        // update the event cameras according to new streams to save
        const newEventCameras = [];
        let camerasUpdated = false;
        for (const eventCamera of originalEvent.cameras) {
          const { id, streams } = eventCamera;
          const savedStream = streams.find((stream) => SaveClipUtils.isStreamMarkedSaved(stream));
          const clipVideoCamera = camerasVideoSourceMap[id];
          const clipVideoStreamName = clipVideoCamera?.streamName;
          if (clipVideoStreamName && savedStream?.name !== clipVideoStreamName) {
            const newEventCamera = cloneDeep(eventCamera);
            const newVideoStream = newEventCamera.streams.find(
              (stream) => stream.id === clipVideoCamera.streamId,
            );
            if (!newVideoStream) {
              throw new Error('Missing video stream');
            }
            newVideoStream.totalSegments = INITIAL_SEGMENTS_NUMBER;
            newEventCameras.push(newEventCamera);
            camerasUpdated = true;
          }
        }

        // Update the event data and push video to cloud
        const payload = {
          pushToCloudStatus: CLOUD_STATUS_IN_PROGRESS as PushToCloudStatus,
          ...(camerasUpdated ? { cameras: newEventCameras } : {}),
        };
        let updatedEvent: IEvent;
        this.handleUpdateEvent(payload, originalEvent)
          .then((event: IEvent) => {
            updatedEvent = event;
            return this.requestVmsPushToCloud({
              shareStartTime,
              shareEndTime,
              event,
            });
          })
          .then(() => {
            this.startCloudPolling(updatedEvent);
          })
          .catch((error) => {
            console.error('handleUpdateEvent: ', error);
            this.handleUpdateEvent({ pushToCloudStatus: CLOUD_STATUS_ERROR }, originalEvent);
          });
      },
    );
  };

  notifyEmailRecipients = async () => {
    const { locale } = this.props;
    const { originalEvent } = this.state;
    try {
      if (!originalEvent?.id) {
        throw new Error('Missing event id');
      }
      const event = await CloudVmsApi().updateSharedEvent(
        { eventId: originalEvent.id },
        { pushToCloudStatus: CLOUD_STATUS_COMPLETE },
      );
      this.setState({ originalEvent: event });
      this.syncEvent(event);
      toast.success(locale.faceblur.emailsSentSuccessfully);
    } catch {
      toast.error(locale.faceblur.failedToSendEmails);
    }
  };

  cancelPreviousPlaylistRequest = (cameraId: string) => {
    if (this.requestAbortControllersByCameraId[cameraId]) {
      this.requestAbortControllersByCameraId[cameraId].abort(
        `Playlist request cancelled due to new request.`,
      );
    }
  };

  setNewPlaylistRequestAbortController = (cameraId: string) => {
    const abortController = new AbortController();
    const abortControllerSignal = abortController.signal;
    this.requestAbortControllersByCameraId[cameraId] = abortController;
    return abortControllerSignal;
  };

  resetPlaylistRequestAbortController = (cameraId: string) => {
    if (this.requestAbortControllersByCameraId[cameraId]) {
      delete this.requestAbortControllersByCameraId[cameraId];
    }
  };

  updateIsCheckingVideo = (value: boolean) => {
    this.setState({ isCheckingVideo: value });
  };

  startCloudPolling = (event: IEvent, durationChanged = false) => {
    this.setState(
      {
        pollingState: CLOUD_STATUS_IN_PROGRESS,
        displaySaveAsInProgress: true,
        uploadingPercentage: null,
      },
      () => {
        const { locale, accessibleAddresses, siteCamerasById } = this.props;
        toast.success(locale.messages.saveStarted);

        if (SaveClipUtils.allAvailableVideoInCloud(this.state.camerasVideoSourceMap)) {
          // There is a potential problem with polling for share cloud video
          // Since cloud video can be merged and prepared, the cloud VMS ignores the stream the request specifies.
          // So if VD poll the stream that doesn't really have video, the polling will never finish.
          // So just stop polling for cloud video for now
          this.handleSaveComplete();
        } else {
          EventActions.cloudPollStart(
            event,
            accessibleAddresses,
            siteCamerasById,
            'cloud-panel',
            durationChanged,
          );
        }
      },
    );
  };

  areSameKeys = (obj1: { [key: string]: any }, obj2: { [key: string]: any }) => {
    return JSON.stringify(keys(obj1).sort()) === JSON.stringify(keys(obj2).sort());
  };

  /** Get cameras that can't be removed from this clip and the ones that can be added to this clip.
   *  This function return new camera collections, it doesn't modify the original ones.
   */
  processCameras(siteCameras: CallhomeCamera[], event: IEvent | undefined, time: number) {
    const { user, playingCameras } = this.props;

    const cameras: SavePanelCamerasById = {};
    const relevantCameras: SavePanelCamera[] = [];
    const otherCameras: SavePanelCamera[] = [];

    const snapshots = CameraUtil.getSnapshots(user.profile.customerName, playingCameras, time);
    const snapshotsByCHCameraId: { [chCameraId: string]: string } = {};
    for (const snapshot of snapshots) {
      snapshotsByCHCameraId[snapshot.playingCamera.chCameraId] = snapshot.imageURL;
    }

    const eventCameraIds: string[] = [];
    const eventCameraThumbnails: { [key: string]: string } = {};

    if (event) {
      for (const camera of event.cameras || []) {
        eventCameraIds.push(camera.id);
        eventCameraThumbnails[camera.id] = camera.thumbnail;
      }
    }

    for (const camera of siteCameras) {
      const cameraId = camera.cameraId;

      let thumbnailType: ThumbnailType = THUMBNAIL_TYPE_CAMERA;
      let thumbnail = camera.thumbnail || '';

      if (eventCameraThumbnails[cameraId]) {
        thumbnail = eventCameraThumbnails[cameraId];
        thumbnailType = THUMBNAIL_TYPE_EVENT;
      } else if (snapshotsByCHCameraId[camera.id]) {
        thumbnail = snapshotsByCHCameraId[camera.id];
        thumbnailType = THUMBNAIL_TYPE_SNAPSHOT;
      }

      const storedCamera: SavePanelCamera = {
        id: cameraId,
        chId: camera.id,
        deviceId: camera.deviceId,
        name: camera.name,
        thumbnail,
        thumbnailType,
        streams: [],
      };

      camera.streams.forEach((stream) => {
        const { id, name, average_bps, earliestSegmentDate, cloudVMS, enableMotionlessRetention } =
          stream;
        const eventCamera = event?.cameras.find((entry) => entry.id === cameraId);
        const eventCameraStream = eventCamera?.streams.find((entry) => entry.id === id);
        storedCamera.streams.push({
          id,
          name,
          average_bps,
          cloudVMS,
          earliestSegmentDate: earliestSegmentDate as string | undefined,
          totalSegments: eventCameraStream?.totalSegments,
          enableMotionlessRetention,
        });
      });

      const cameraIsPlaying = !!playingCameras.find((entry) => entry.chCameraId === camera.id);
      if (cameraIsPlaying || includes(eventCameraIds, cameraId)) {
        relevantCameras.push(storedCamera);

        if ((event && eventCameraIds.includes(cameraId)) || !event) {
          cameras[cameraId] = storedCamera;
        }
      } else {
        otherCameras.push(storedCamera);
      }
    }

    return {
      selectedCameras: cameras,
      availableCameras: concat(relevantCameras, otherCameras),
    };
  }

  getDownloadStreamIds() {
    const { originalEvent } = this.state;
    let downloadStreamIds: string[] = [];

    if (originalEvent) {
      if (originalEvent.downloads) {
        downloadStreamIds = map(originalEvent.downloads, (download) => {
          return download.streamId;
        });
      } else {
        forEach(originalEvent.cameras, (camera) => {
          const { streams } = camera;
          forEach(streams, (stream) => {
            if (Object.hasOwnProperty.call(stream, 'totalSegments')) {
              downloadStreamIds.push(stream.id);
            }
          });
        });
      }
    }

    return downloadStreamIds;
  }

  updateCamSelection = (cameras: SavePanelCamerasById) =>
    this.setState({ selectedCameras: cameras });

  updateCameraSelectorOpen = (isSelectorOpen: boolean) => {
    this.setState({ isCameraSelectorOpen: isSelectorOpen });
  };

  onDateChange = (fieldName: string, value: Date | undefined) => {
    const { startTime, endTime, camerasVideoSourceMap, shouldUseBestQuality } = this.state;
    let newStartTime = startTime;
    let newEndTime = endTime;
    let newShouldUseBestQuality = shouldUseBestQuality;
    if (!value) {
      throw new Error('Date value is undefined');
    }
    switch (fieldName) {
      case 'start-time': {
        newStartTime = value;
        break;
      }
      case 'end-time': {
        newEndTime = value;
        if (SaveClipUtils.onlySDVideoAvailable(camerasVideoSourceMap, newEndTime)) {
          newShouldUseBestQuality = false;
        }
        break;
      }
      default:
        throw new Error(`Unhandled fieldName: ${fieldName}`);
    }
    const timeValidationStates = this.validateTime(newStartTime, newEndTime);
    this.setState({
      startTime: newStartTime,
      endTime: newEndTime,
      shouldUseBestQuality: newShouldUseBestQuality,
      ...timeValidationStates,
    });
  };

  onChange = (fieldName: string, value: string | boolean | VISIBILITY_TYPE) => {
    switch (fieldName) {
      case 'title': {
        const title = CommonUtil.trimLeadingSpaces(value as string);
        this.setState({
          title,
        });
        this._validateTitle(true, title);
        break;
      }
      case 'fill-gaps': {
        this.setState({ fillGaps: value as boolean });
        break;
      }
      case 'best-quality': {
        this.updateBestQuality(value as boolean);
        break;
      }
      case 'visibility': {
        this.setState({ visibility: value as VISIBILITY_TYPE });
        break;
      }
      case 'description': {
        const description = (value as string) || '';
        this.setState({
          description: description.substr(0, MAX_EVENT_DESCRIPTION_LENGTH),
        });
        break;
      }
      case 'ifShare': {
        const { ifShare } = this.state;

        this.setState(
          {
            ifShare: !ifShare,
          },
          () => {
            if (!ifShare) {
              // scroll to display share details fields
              const bodyContent = this.bodyContentRef.current;
              if (bodyContent) {
                bodyContent.scrollTop = bodyContent.scrollHeight;
              }
            }
          },
        );
        break;
      }
      case 'emails': {
        const emails = (value as string).toLowerCase() || '';
        this.setState({
          emails,
        });
        this._validateEmail(true, emails, true);
        break;
      }
      case 'shareMessage': {
        const message = (value as string) || '';
        this.setState({
          shareMessage: message.substr(0, MAX_LENGTH_SHARE_MESSAGE),
        });
        break;
      }

      case 'save-motion-only-video': {
        const saveMotionOnlyVideo = value as boolean;
        this.setState({
          saveMotionOnlyVideo,
        });
        break;
      }

      case 'faceblur': {
        const faceblur = value as boolean;
        this.setState({
          faceblur,
        });
        break;
      }
    }
  };

  updateBestQuality = (value: boolean) => this.setState({ shouldUseBestQuality: value });

  updateCameraVideoSourceMapAndBestQuality = (
    camerasVideoSourceMap: CamerasVideoSourceMap,
    endTime: Date | undefined,
  ) => {
    const { shouldUseBestQuality } = this.state;
    let newShouldUseBestQuality = shouldUseBestQuality;
    if (endTime && SaveClipUtils.onlySDVideoAvailable(camerasVideoSourceMap, endTime)) {
      newShouldUseBestQuality = false;
    }
    this.setState({ camerasVideoSourceMap, shouldUseBestQuality: newShouldUseBestQuality });
  };

  getDuration = (start: string | number | Date, end: string | number | Date) =>
    new Date(end).getTime() - new Date(start).getTime();

  isLongDuration = (start: string | number | Date, end: string | number | Date) => {
    const length = FeaturesUtil.hasNoFullAccessInKiosk()
      ? // To Do: This used to be 5, but because The Gap is complaining, we are bumping it to 60
        // A better fix would be to lift the restrictions altoghter in kiosk mode, but I don't know
        // how it is going to play out, so I'm only changing this value here in case we want to
        // do more adjustments
        60
      : 60;

    return this.getDuration(start, end) >= length * 60 * 1000;
  };

  isShortDuration = (
    start: string | number | Date | undefined,
    end: string | number | Date | undefined,
  ) => !!start && !!end && this.getDuration(start, end) < MIN_DURATION_MS;

  onBlur = (fieldName: string) => {
    switch (fieldName) {
      case 'title': {
        const title = this.state.title?.trim();

        this._validateTitle(true, title);
        this.setState({
          title,
        });
        break;
      }
      case 'description':
        this.setState({
          showCharCountDescription: false,
        });
        break;
      case 'emails':
        this._validateEmail(true);
        break;
      case 'shareMessage':
        this._validateEmail(true);
        this.setState({
          showCharCountShareMessage: false,
        });
        break;
    }
  };

  _onKeyPressEmail = (e: React.KeyboardEvent) => {
    switch (this.keyboard.getKeyPressed(e)) {
      case KEYS.ENTER_KEY.name:
      case KEYS.SPACE_KEY.name:
      case KEYS.COMMA_KEY.name:
      case KEYS.SEMICOLON_KEY.name: {
        e.preventDefault();
        this._addShare();
        break;
      }
    }
  };

  _validateTitle(setErrorState = false, title = this.state.title) {
    const { locale } = this.props;
    const isValid = !!title && title.length > 0;

    if (setErrorState) {
      this.setState({
        errorTitle: isValid ? '' : locale.error.titleRequired,
      });
    }

    return isValid;
  }

  validateTime = (startTime: Date | undefined, endTime: Date | undefined) => {
    const { locale } = this.props;
    const shortDuration = this.isShortDuration(startTime, endTime);

    let errorTime = '';

    if (moment(endTime).isBefore(startTime)) {
      errorTime = locale.cloudShare.endTimeBeforeStartTime;
    } else if (shortDuration) {
      errorTime = locale.cloudShare.durationShort;
    } else if (
      startTime &&
      endTime &&
      this.getDuration(startTime, endTime) > TWENTY_FOUR_HOURS_MS
    ) {
      errorTime = locale.cloudShare.durationOverLimit;
    } else if (moment(startTime).isAfter(new Date())) {
      errorTime = locale.cloudShare.startTimeInFuture;
    } else if (moment(endTime).isAfter(new Date())) {
      errorTime = locale.cloudShare.endTimeInFuture;
    }
    return {
      errorTime,
      longDuration: !!startTime && !!endTime && this.isLongDuration(startTime, endTime),
      shortDuration,
    };
  };

  _validateEmail(setErrorState = false, emails = this.state.emails, isOnChange = false) {
    const { locale } = this.props;
    const { contextMode, saveMode, saveExistingEvent, errorEmail, shares } = this.state;

    let isValid = true;

    if (contextMode === CONTEXT_SAVETOCLOUD) {
      let errorMessage = '';

      // Can be empty on edit, but required for create
      isValid =
        (!saveMode && !saveExistingEvent) ||
        (saveExistingEvent && !isEmpty(emails)) ||
        (saveMode && !isEmpty(emails)) ||
        !isEmpty(shares);

      if (!isValid) {
        errorMessage = locale.error.emailsAtLeastOne;
      } else if (!isEmpty(emails)) {
        const emailList = emails.split(this.emailSplitOn);

        forEach(emailList, (email) => {
          if (email.length === 0) {
            return;
          }

          email = email.trim();

          if (!email || !this.emailRegex.test(email)) {
            isValid = false;
            return false;
          }
        });

        if (!isValid) {
          errorMessage = locale.error.emailsInvalid;
        }
      }

      // For onChange event only remove error message (if applicable), but don't immediately display it as user might just have started typing
      if (setErrorState && (!isOnChange || (isOnChange && !isEmpty(errorEmail)))) {
        this.setState({
          errorEmail: errorMessage,
        });
      }
    }

    return isValid;
  }

  syncEvent = (event: IEvent) => {
    const { isSaveTimelineEvent, syncTimelineEvent, updateEvent } = this.props;

    if (isSaveTimelineEvent) {
      syncTimelineEvent(event);
    } else {
      const newEvent = clone(event);
      if (updateEvent) updateEvent(newEvent);
    }
  };

  async loadLatestEvent(event: IEvent, isComplete = false) {
    try {
      const { id: eventId } = event;
      const latestEvent = await PlatformApi().getEvent(eventId);
      this.handleEventLoaded(latestEvent, isComplete);
      this.syncEvent(latestEvent);
    } catch (err) {
      const { locale } = this.props;
      console.error('Load latest event error:', err);

      // Use provided event instead
      this.handleEventLoaded(event);
      toast.error(locale.error.loadLatest);
    }

    this.stopLoading();
  }

  handleEventLoaded(event: IEvent, isComplete = false) {
    const { editMode, locale, accessibleAddresses, siteCamerasById } = this.props;
    const { selfShare } = this.state;
    const { contextMode: sourceContextMode, displaySaveAsInProgress } = this.state;
    let downloadStreams = {};
    let contextMode = clone(sourceContextMode);

    if (EventUtil.isDownloadBookmark(event)) {
      contextMode = CONTEXT_DOWNLOAD;
      downloadStreams = this._parseDownloadStreams(event);
      EventActions.connectPollStart(event, accessibleAddresses, siteCamerasById);
    } else if (
      contextMode === CONTEXT_SAVETOCLOUD &&
      !isComplete &&
      event.pushToCloudStatus === CLOUD_STATUS_IN_PROGRESS
    ) {
      EventActions.cloudPollStart(event, accessibleAddresses, siteCamerasById, 'cloud-panel');
    }

    let startTime = this.getEventStartTime(event);
    let endTime = this.getEventEndTime(event);
    const durationAsMinutes = moment.duration(moment(endTime).diff(moment(startTime))).asMinutes();

    if (DateHandler.isSame(startTime, endTime) || durationAsMinutes < 1) {
      /** Add 1 minutes at the beginning and the end of the event
       * if the event is less than 1 minute long
       */
      endTime = DateHandler.addMinutesToDate(1, endTime);
      startTime = DateHandler.subtractMinutesFromDate(1, startTime);
    }

    const isInProgress = PollCloudStore.isInProgress(event) || displaySaveAsInProgress;
    const ifShare = this.checkIfShare(event);
    const cloudStatusError = event.pushToCloudStatus === CLOUD_STATUS_ERROR;
    const eventSelfShare = event.shares ? SaveClipUtils.getSelfShare(event.shares as Share[]) : {};

    const newState: Partial<SavePanelState> = {
      contextMode,
      readOnly: isInProgress,
      editMode: !isInProgress && !!editMode,
      originalEvent: cloneDeep(event),
      title: event.title,
      description: EventUtil.getDescription(event),
      ifShare,
      selfShare: { ...selfShare, ...eventSelfShare },
      shares: EventUtil.isShared(event) ? clone(event.shares as Share[]) : [],
      shareMessage: EventUtil.getShareMessage(event),
      startTime,
      endTime,
      downloadStreams,
      visibility: SaveClipUtils.getClipVisibility(event) ?? VISIBILITY_PUBLIC,
    };

    if (!isComplete) {
      if (cloudStatusError) {
        this.setState({ cloudErrorNotification: true });
        toast.error(locale.error.saveClip, {
          autoClose: false,
          onClose: () => this.setState({ cloudErrorNotification: false }),
        });
      }
    }

    this.setState(newState as SavePanelState);
  }

  _addShare() {
    const { emails, shares, diffShares } = this.state;

    const validEmails = emails.split(this.emailSplitOn).filter((email) => {
      const trimmedEmail = email.trim();
      return trimmedEmail.length > 0 && this.emailRegex.test(trimmedEmail);
    });

    // Only proceed if there are valid emails
    if (validEmails.length > 0) {
      const newShares = validEmails.map((email) => this._parseShareEmail(email));

      this.setState({
        emails: '',
        shares: uniqBy(concat(shares, ...(newShares as Partial<Share>[])), 'email') as Share[],
        diffShares: uniqBy(concat(diffShares, ...newShares), 'email'),
      });
    }
  }

  removeShare = (selectedShare: Share) => {
    const { shares, diffShares } = this.state;
    const shareIdx = findIndex(shares, ['email', selectedShare.email]); // keep track of all current shares with any changes (for edit mode)
    const diffShareIdx = findIndex(diffShares, ['email', selectedShare.email]); // Keep track of shares diff (delta) to make editing easier
    const newState: Partial<SavePanelState> = {};

    if (shareIdx !== -1) {
      const newShares = cloneDeep(shares);
      newShares.splice(shareIdx, 1);
      newState.shares = newShares;
    }

    if (diffShareIdx !== -1) {
      const newDiffShares = cloneDeep(diffShares);
      newDiffShares.splice(diffShareIdx, 1);
      newState.diffShares = newDiffShares;
    }

    if (!isEmpty(newState)) {
      this.setState(newState as SavePanelState);
    }
  };

  saveExistingEvent = () => {
    const { locale } = this.props;
    const {
      camerasVideoSourceMap,
      description,
      diffShares,
      emails: emailsState,
      endTime,
      originalEvent,
      shareMessage,
      shares,
      startTime,
      visibility,
    } = this.state;

    let hasDurationChanges = false;
    const hasCameraChanges = false;
    let hasShareChanges = false;

    if (!originalEvent) {
      throw new Error('No existing event to save');
    }

    const editEvent = cloneDeep(originalEvent); // Up-to-date version of the event to initiate push-to-cloud (not all properties needed, but this makes it more robust)
    const editDetails: Partial<IEvent> = {}; // Payload
    const hasExistingShares = EventUtil.isShared(originalEvent);
    const eventCameras = SaveClipUtils.generateNewEventCamerasPayload(
      camerasVideoSourceMap,
    ) as EventCamera[];

    this.setState({
      isLoading: true,
    });

    editEvent.pushToCloudStatus = CLOUD_STATUS_IN_PROGRESS;
    editEvent.cameras = eventCameras;

    editDetails.pushToCloudStatus = CLOUD_STATUS_IN_PROGRESS;
    editDetails.cameras = eventCameras;

    // Changes specific to sharing: editing an event with existing shares or adding shares to e.g. a transaction event
    const emails = emailsState
      ? emailsState.split(this.emailSplitOn).filter((e) => e.length > 0)
      : [];
    shares.forEach((share) => {
      const { sharedBy, email } = share;
      if (sharedBy !== email) {
        emails.push(email);
      }
    });

    if (!isEmpty(diffShares)) {
      hasShareChanges = true;
    }

    editDetails.shares = this._parseShares();
    editEvent.shares = this._parseShares();

    if (originalEvent.details?.shareMessage !== shareMessage) {
      set(editDetails, ['details', 'shareMessage'], shareMessage);
      editEvent.details = editEvent.details || {};
      editEvent.details.shareMessage = shareMessage;
    }

    // Set start time and status if the time has changed or if this is the first time the event is shared (e.g. transactions)
    if (
      startTime &&
      (!hasExistingShares || !DateHandler.isSame(originalEvent.startTime, startTime))
    ) {
      hasDurationChanges = true;
      const startTimeUtc = DateHandler.toUtcString(startTime);
      editDetails.shareStartTime = editEvent.shareStartTime = startTimeUtc;
    }

    // Set end time and status if the time has changed or if this is the first time the event is shared (e.g. transactions)
    if (endTime && (!hasExistingShares || !DateHandler.isSame(originalEvent.endTime, endTime))) {
      hasDurationChanges = true;
      const endTimeUtc = DateHandler.toUtcString(endTime);
      editDetails.shareEndTime = editEvent.shareEndTime = endTimeUtc;
    }

    // Don't add description field unnecessarily if there is no existing description
    const originalDescription = originalEvent.details?.description;

    if (originalDescription !== description) {
      set(editDetails, ['details', 'description'], description); //editDetails.details is undefined at this moment
      editEvent.details = editEvent.details || {};
      editEvent.details.description = description;
    }

    const originalVisibility = SaveClipUtils.getClipVisibility(originalEvent);
    if (visibility !== originalVisibility) {
      const newVisibility = visibility || SaveClipUtils.getClipDefaultVisibility();
      editEvent.scope = { visibility: newVisibility };
    }

    if (hasDurationChanges || hasCameraChanges) {
      const { shareStartTime, shareEndTime } = editEvent;

      if (!shareStartTime || !shareEndTime) {
        throw new Error('Missing shareStartTime or shareEndTime');
      }

      // Update the event data and push video to cloud
      let updatedEvent: IEvent;
      this.handleUpdateShares({ payload: editDetails })
        .then((event: IEvent) => {
          updatedEvent = event;
          return this.requestVmsPushToCloud({
            shareStartTime,
            shareEndTime,
            event,
          });
        })
        .then(() => {
          this.logAnalytics({
            event: updatedEvent,
            context: 'saveToCloud',
            success: true,
            source: '#saveExistingEvent',
          });
          this.startCloudPolling(updatedEvent, hasDurationChanges);
        })
        .catch((error: Error) => {
          console.error('handleUpdateShares: ', error);
          this.logAnalytics({
            event: editEvent,
            context: 'saveToCloud',
            success: false,
            source: '#saveExistingEvent',
            errorMessage: error.message,
          });
          this.handleUpdateEvent({ pushToCloudStatus: CLOUD_STATUS_ERROR }, editEvent);
        });
    } else if (hasShareChanges) {
      // Use Cloud VMS endpoint to ensure that emails are sent
      this.handleUpdateShares({
        payload: editDetails,
        message: locale.messages.updateSuccess,
      });
    } else {
      // Only update the event data
      this.handleUpdateEvent(editDetails);
    }
  };

  generateNewClipEvent = (context: 'saveToCloud' | 'download') => {
    const {
      camerasVideoSourceMap,
      fillGaps,
      selectedCameras,
      title,
      startTime,
      endTime,
      description,
      shareMessage,
      visibility,
      faceblur,
    } = this.state;
    const { user } = this.props;

    const { sub, features } = user.jwtUser;
    const cameras = Object.values(selectedCameras);
    const [firstCam] = cameras;
    const newClipCameras = SaveClipUtils.generateNewEventCameras({
      camerasVideoSourceMap,
      selectedCamerasDict: selectedCameras,
    });

    // Don't need to specify all the time fields (Platform API handles this)

    const event: ModifiedIEvent = {
      cameras: newClipCameras,
      details: {
        description,
        favourite: true,
        favouritedBy: sub ? [sub] : [],
      },
      endTimeLocal: endTime
        ? moment(endTime).utc().tz(this.timezoneId).format(DateHandler.FORMAT_UTC)
        : '',
      locationId: firstCam.deviceId,
      primaryCameraId: firstCam.id,
      restrictions: {
        downloads: features?.downloads === undefined ? true : features?.downloads,
        audio: features?.audio === undefined ? true : features?.audio,
      },
      scope: {
        visibility,
      },
      startTimeLocal: startTime
        ? moment(startTime).utc().tz(this.timezoneId).format(DateHandler.FORMAT_UTC)
        : '',
      subtitle: `${firstCam.name} @ ${this.locationName}`,
      title,
      type: EVENT_TYPE_BOOKMARK,
      videos: [],
      siteId: this.locationId,
    };

    switch (context) {
      case 'saveToCloud':
        event.shares = this._parseShares();
        event.details.shareMessage = shareMessage;
        event.pushToCloudStatus = CLOUD_STATUS_IN_PROGRESS;
        event.shareStartTime = startTime ? DateHandler.toUtcString(startTime) : '';
        event.shareEndTime = endTime ? DateHandler.toUtcString(endTime) : '';
        event.restrictions.fillGaps = fillGaps;
        event.restrictions.faceblur = faceblur;
        break;
      case 'download':
        event.subtype = EVENT_SUBTYPE_DOWNLOAD;
        break;
    }

    return event;
  };

  saveNewClipToCloud = () => {
    this.setState(
      {
        isLoading: true,
        contextMode: CONTEXT_SAVETOCLOUD,
      },
      () => {
        const { locale, user, playerType } = this.props;
        const context = 'saveToCloud';
        const event = this.generateNewClipEvent(context);
        const { shareStartTime, shareEndTime } = event;
        // Don't pin events when saving clips in video search player
        const switchToEvent = this.props.playingCameras.length > 0 && playerType !== 'videoSearch';

        if (!shareStartTime || !shareEndTime) {
          throw new Error('Missing shareStartTime or shareEndTime');
        }

        if (!user.s3) {
          throw new Error('Missing s3');
        }

        EventActions.createEvent(
          { bookmark: event, switchToEvent },
          (err: Error, createdEvent: IEvent) => {
            if (err) {
              this.logAnalytics({
                event: event as any,
                context,
                success: false,
                source: '#saveNewClipToCloud',
                errorMessage: err.message,
              });
              toast.error(locale.error.bookmarkCreate);
              return;
            }

            const { shares } = createdEvent;

            this.handleNewClipEventCreated(createdEvent, context);

            this.setState({
              selfShare: SaveClipUtils.getSelfShare(shares as Share[]) as ModifiedShare,
              shares: isEmpty(shares) ? [] : (clone(shares) as Share[]),
              diffShares: [],
              shareMessage: EventUtil.getShareMessage(createdEvent),
            });

            this.requestVmsPushToCloud({
              shareStartTime,
              shareEndTime,
              event: createdEvent,
            })
              .then(() => {
                this.logAnalytics({
                  event: createdEvent,
                  context: 'saveToCloud',
                  success: true,
                  source: '#saveNewClipToCloud',
                });
                this.startCloudPolling(createdEvent);

                if (this.props.playerType === 'videoSearch' && this.props.onPushToCloud) {
                  this.props.onPushToCloud();
                }
              })
              .catch((error: Error) => {
                console.error('requestVmsPushToCloud:', error);
                this.logAnalytics({
                  event: createdEvent,
                  context: 'saveToCloud',
                  success: false,
                  source: '#saveNewClipToCloud',
                  errorMessage: error.message,
                });
                this.handleUpdateEvent({ pushToCloudStatus: CLOUD_STATUS_ERROR }, createdEvent);
              });
          },
          true,
          user.s3,
        );
      },
    );
  };

  openDownloadConfirm = () => this.setState({ confirmModalOpen: true });

  closeDownloadConfirm = () => this.setState({ confirmModalOpen: false });

  downloadInStore = () => {
    const { user, playingCameras, accessibleAddresses, playerType, siteCamerasById } = this.props;
    this.setState(
      {
        isLoading: true,
        contextMode: CONTEXT_DOWNLOAD,
        confirmModalOpen: false,
      },
      () => {
        const event = this.generateNewClipEvent('download');
        // Don't pin events when downloading clips in video search player
        const switchToEvent = playingCameras.length > 0 && playerType !== 'videoSearch';
        event.downloads = SaveClipUtils.generateNewEventDownloads(this.state.camerasVideoSourceMap);

        if (!user.s3) {
          throw new Error('Missing s3');
        }

        EventActions.createEvent(
          { bookmark: event, switchToEvent },
          (err: Error, createdEvent: IEvent) => {
            const { locale } = this.props;
            if (err) {
              this.logAnalytics({
                event: event as any,
                context: 'download',
                success: false,
                source: '#downloadInStore',
                errorMessage: err.message,
              });
              // TODO: need to change the error message
              toast.error(locale.error.bookmarkCreate);
              return;
            }

            this.handleNewClipEventCreated(createdEvent, 'download');

            const downloadStreams = this._parseDownloadStreams(createdEvent);
            Object.values(downloadStreams).forEach((downloadStream) => {
              downloadStream.state = CONNECT_DOWNLOAD_STATUS_ENQUEUED;
            });

            // TODO: fix type
            this.setState({
              downloadStreams: downloadStreams as { [key: string]: DownloadStream },
            });

            this.requestConnectDownload(createdEvent)
              .then(() => {
                toast.success(locale.messages.downloadStarted);
                EventActions.connectPollStart(createdEvent, accessibleAddresses, siteCamerasById);
              })
              .catch(() => {
                toast.error(locale.error.downloadStart);
              });
          },
          true,
          user.s3,
        );
      },
    );
  };

  handleNewClipEventCreated(createdEvent: IEvent, context: 'saveToCloud' | 'download') {
    this.logAnalytics({
      event: createdEvent,
      context,
      success: true,
      source: '#handleNewClipEventCreated',
    });
    this.syncEvent(createdEvent);

    const { selectedCameras } = this.state;
    const { cameras, title } = createdEvent;
    const newSelectedCameras = this.updateThumbnails(selectedCameras, cameras);
    const visibility = SaveClipUtils.getClipVisibility(createdEvent) ?? VISIBILITY_PUBLIC;

    this.setState({
      description: EventUtil.getDescription(createdEvent),
      emails: '',
      fillGaps: EventUtil.getFillGaps(createdEvent),
      faceblur: EventUtil.getFaceblur(createdEvent),
      isLoading: false,
      originalSelectedCameras: newSelectedCameras,
      originalEvent: cloneDeep(createdEvent),
      saveMode: false,
      selectedCameras: newSelectedCameras,
      title,
      visibility,
    });
  }

  _parseShareEmail = (email: string) => {
    const { user } = this.props;
    const { email: sharedBy, features } = user.jwtUser;
    const sharedAt = DateHandler.getTimestamp();
    const expiration = DateHandler.getTimestamp(
      DateHandler.addDaysToDate(SHARE_EXPIRATION_DAYS, new Date()),
    );

    return {
      email,
      sharedBy,
      sharedAt,
      expiration,
      restrictions: {
        downloads: features?.downloads === undefined ? true : features?.downloads,
        audio: features?.audio === undefined ? true : features?.audio,
        includeShareUrl: FeaturesUtil.canShareClips(),
      },
    };
  };

  _parseShares = () => {
    const { emails, originalEvent, shares: sharesState } = this.state;
    const { user } = this.props;
    const sharedBy = user.jwtUser.email;

    const originalShares = originalEvent?.shares || [];
    let shares = uniqBy(concat(originalShares, sharesState), 'email');

    // Remove any shares with empty email addresses.
    shares = shares.filter((share) => share.email.length !== 0);

    if (emails.length && !shares.find((share) => share.email === emails)) {
      shares.push(this._parseShareEmail(emails));
    }

    // Add originator (user that initiated share) for audit purposes
    if (!shares.find((share) => share.email === sharedBy)) {
      shares.push(this._parseShareEmail(sharedBy));
    }

    return shares;
  };

  /** Save the clip editing changes */
  saveClipChanges = () => {
    const { locale } = this.props;
    const {
      camerasVideoSourceMap,
      originalEvent,
      contextMode,
      emails: emailsState,
      diffShares,
      startTime,
      endTime,
      selectedCameras,
      originalSelectedCameras,
      description,
      shareMessage,
      title,
      visibility,
    } = this.state;

    let hasDurationChanges = false;
    let hasCameraChanges = false;
    let hasShareChanges = false;

    if (!originalEvent) {
      throw new Error('No original event to save changes to');
    }

    this.setState({ isLoading: true });

    // Up-to-date version of the event to initiate push-to-cloud (not all properties needed, but this makes it more robust)
    const editEvent = cloneDeep(originalEvent);

    // Payload should consist only of diff
    const payload: Partial<IEvent> = {};

    // Changes specific to sharing: editing an event with existing shares or adding shares to e.g. a transaction event
    const hasExistingShares = EventUtil.isShared(originalEvent);
    const hasNewlyAddedShare = !hasExistingShares && (!emailsState || !diffShares?.length);

    if (contextMode === CONTEXT_SAVETOCLOUD && (hasExistingShares || hasNewlyAddedShare)) {
      if (hasExistingShares) {
        if (emailsState) {
          diffShares.push(this._parseShareEmail(emailsState));
        }

        hasShareChanges = true;

        payload.shares = diffShares as Share[];

        editEvent.shares = uniqBy(concat(this._parseShares(), payload.shares), 'email');
      } else {
        // TODO: I don't think this is ever going to be true.
        //       We only hit this saveClipChanges function when we're editing an existing event.
        //       Event if we hit this saveClipChanges after we saving a new clip,
        //       we would still have originalEvent as shared because we update this state after clip is created.
        const unprocessedShares = isEmpty(emailsState) ? [] : [this._parseShareEmail(emailsState)];
        payload.shares = uniqBy(concat(unprocessedShares, diffShares as any), 'email');
        payload.cameras = editEvent.cameras;
        editEvent.shares = payload.shares;
        hasShareChanges = true;
      }

      if (originalEvent.details?.shareMessage !== shareMessage) {
        set(payload, ['details', 'shareMessage'], shareMessage);
        editEvent.details = editEvent.details || {};
        editEvent.details.shareMessage = shareMessage;
      }

      const originalStartTime = originalEvent.shareStartTime
        ? originalEvent.shareStartTime
        : originalEvent.startTime;
      // Set start time and status if the time has changed or if this is the first time the event is shared (e.g. transactions)
      if (startTime && (!DateHandler.isSame(originalStartTime, startTime) || !hasExistingShares)) {
        payload.shareStartTime = DateHandler.toUtcString(startTime);
        payload.pushToCloudStatus = CLOUD_STATUS_IN_PROGRESS;
        editEvent.shareStartTime = payload.shareStartTime;
        editEvent.pushToCloudStatus = CLOUD_STATUS_IN_PROGRESS;
        hasDurationChanges = true;
      }

      const originalEndTime = originalEvent.shareEndTime || originalEvent.endTime;
      // Set end time and status if the time has changed or if this is the first time the event is shared (e.g. transactions)
      if (endTime && (!DateHandler.isSame(originalEndTime, endTime) || !hasExistingShares)) {
        payload.shareEndTime = DateHandler.toUtcString(endTime);
        payload.pushToCloudStatus = CLOUD_STATUS_IN_PROGRESS;
        editEvent.shareEndTime = payload.shareEndTime;
        editEvent.pushToCloudStatus = CLOUD_STATUS_IN_PROGRESS;
        hasDurationChanges = true;
      }
    }

    /** We only allow user adding camera when editing clips */
    if (Object.keys(selectedCameras).length > originalEvent.cameras.length) {
      hasCameraChanges = true;
      payload.pushToCloudStatus = CLOUD_STATUS_IN_PROGRESS;
      editEvent.pushToCloudStatus = CLOUD_STATUS_IN_PROGRESS;
    }

    // Changes that can/should only be made to bookmark events
    if (EventUtil.isBookmark(originalEvent)) {
      if (originalEvent.title !== title) {
        payload.title = title;
        editEvent.title = title;
      }

      // Adjust bookmark start and end times if duration changed
      if (payload.shareStartTime) {
        payload.startTimeLocal = new Date(payload.shareStartTime);
        editEvent.startTimeLocal = new Date(payload.shareStartTime);
      }

      if (payload.shareEndTime) {
        payload.endTimeLocal = new Date(payload.shareEndTime);
        editEvent.endTimeLocal = new Date(payload.shareEndTime);
      }
    }

    // Don't add description field unnecessarily if there is no existing description
    const hasDescription = !isEmpty(originalEvent.details?.description);
    if (
      originalEvent.details?.description !== description &&
      (hasDescription || (!hasDescription && !isEmpty(description)))
    ) {
      set(payload, ['details', 'description'], description);
      editEvent.details = editEvent.details || {};
      editEvent.details.description = description;
    }

    const originalVisibility = SaveClipUtils.getClipVisibility(originalEvent);
    if (visibility !== originalVisibility) {
      /** set visibility for both payload and editEvent */
      const newVisibility = visibility || SaveClipUtils.getClipDefaultVisibility();
      payload.scope = { visibility: newVisibility };
      editEvent.scope = { visibility: newVisibility };
    }

    if (hasDurationChanges || hasCameraChanges) {
      const { cameras, shareStartTime, shareEndTime } = editEvent;

      if (!shareStartTime || !shareEndTime) {
        throw new Error('Missing shareStartTime or shareEndTime');
      }

      /** When duration changes or new cameras added, we need to upload new video.
       *  For cameras that need to upload new video, we need to set the video stream's totalSegments to -1 (the initial value).
       */
      const newClipVideoCamerasDict = hasDurationChanges
        ? camerasVideoSourceMap
        : pickBy(camerasVideoSourceMap, (value, key) => !originalSelectedCameras[key]);
      const camerasWithNewVideo = SaveClipUtils.generateNewEventCameras({
        camerasVideoSourceMap: newClipVideoCamerasDict,
        selectedCamerasDict: selectedCameras,
      });

      payload.cameras = camerasWithNewVideo;
      editEvent.cameras = hasDurationChanges
        ? camerasWithNewVideo
        : cameras.concat(camerasWithNewVideo);

      // Push video to cloud and update the event data
      let updatedEvent: IEvent;
      this.handleUpdateShares({ payload })
        .then((event) => {
          updatedEvent = event;
          return this.requestVmsPushToCloud({
            shareStartTime,
            shareEndTime,
            event,
          });
        })
        .then(() => {
          // TODO: seems pulling all the streams including the previous existing ones
          this.startCloudPolling(updatedEvent, hasDurationChanges);
        })
        .catch(() => {
          this.handleUpdateEvent({ pushToCloudStatus: CLOUD_STATUS_ERROR }, editEvent);
          this.stopLoading();
        });
    } else if (hasShareChanges) {
      // Use Cloud VMS endpoint to ensure that emails are sent
      this.handleUpdateShares({
        payload,
        message: locale.messages.updateSuccess,
      });
    } else {
      // Only update the event data
      this.handleUpdateEvent(payload, editEvent, true);
    }
  };

  logAnalytics({
    event,
    context,
    success = true,
    source,
    errorMessage,
  }: {
    event: IEvent;
    context: 'saveToCloud' | 'download';
    success: boolean;
    source: string;
    errorMessage?: string;
  }) {
    const { cameras, startTime, endTime, shares } = event;
    const { shouldUseBestQuality, saveMotionOnlyVideo, camerasVideoSourceMap, faceblur } =
      this.state;
    const {
      cameraIds,
      cameraNames,
      streamNames,
      sources: videoSources,
    } = SaveClipUtils.getCameraAnalyticsInfo(camerasVideoSourceMap);

    const params: {
      event: IEvent;
      isBestQuality: boolean;
      cameraCount: number;
      camera360Count: number;
      cameraIds: string;
      cameraNames: string;
      durationInSec: number;
      isSuccess: boolean;
      saveMotionOnlyVideo: boolean;
      shareCount?: number;
      streamNames: string;
      videoSources: string;
      source: string;
      errorMessage: string;
      faceblur: boolean;
      faceblurDurationInMin: number;
    } = {
      event,
      isBestQuality: shouldUseBestQuality,
      cameraCount: cameras.length,
      camera360Count: cameras.filter((camera) => CameraUtil.is360(camera)).length,
      cameraIds,
      cameraNames,
      durationInSec: (new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000,
      isSuccess: success,
      saveMotionOnlyVideo,
      streamNames,
      videoSources,
      source,
      errorMessage: errorMessage ?? '',
      faceblur,
      faceblurDurationInMin: faceblur
        ? Math.round(this.getFaceblurClipDuration() / (1000 * 60))
        : 0,
    };

    switch (context) {
      case 'saveToCloud':
        params.shareCount = shares ? shares.length - 1 : 0;
        AnalyticsLogger.createSaveAndShare(params as any);
        break;
      case 'download':
        AnalyticsLogger.createLocalDownload(params);
    }
  }

  getPushToCloudStatusForOneCam = ({
    eventId = this.state.originalEvent?.id,
    deviceId,
    cameraId,
    streamId,
    start = this.state.startTime?.getTime(),
    end = this.state.endTime?.getTime(),
  }: {
    eventId?: string;
    deviceId: DeviceId;
    cameraId: string;
    streamId: string;
    start?: number;
    end?: number;
  }) => {
    if (!eventId) {
      console.error('No event to check push to cloud status');
      return Promise.reject('No event to check push to cloud status');
    }
    if (!start || !end) {
      console.error('Missing start or end time');
      return Promise.reject('Missing start or end time');
    }

    const { user } = this.props;
    return SaveClipUtils.getPushToCloudStatus({
      tenantId: user.customerId,
      eventId,
      deviceId,
      cameraId,
      streamId,
      start,
      end,
    });
  };

  requestVmsPushForOneCam = (camera: CameraVideoSource, event?: IEvent) => {
    const { accessibleAddresses, event: eventProp, user, siteCamerasById } = this.props;
    const { fillGaps, saveMotionOnlyVideo = false, startTime, endTime, faceblur } = this.state;
    const { source } = camera;
    const clipEvent = event || eventProp;

    if (!startTime || !endTime) {
      console.error('Missing start or end time');
      return Promise.reject('Missing start or end time');
    }

    // Get the camera's device info to find the correct accessible address
    const cameraDetails = camera.chId && siteCamerasById[camera.chId];
    const accessibleAddress = cameraDetails
      ? getAccessibleAddressForCamera(cameraDetails, accessibleAddresses)
      : undefined;

    if (!accessibleAddress) {
      console.error('Cannot find accessible address for camera device:', camera.id);
      return Promise.reject('Cannot find accessible address for camera device');
    }

    if (!clipEvent) {
      console.error('No clip to attach the pushed video to');
      return Promise.reject('No clip to attach the pushed video to');
    }

    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();

    if (source === VIDEO_SOURCE_LOCAL) {
      return SaveClipUtils.pushLocalVideo({
        start,
        end,
        event: clipEvent,
        camera,
        accessibleAddress,
        fillGaps,
        saveMotionOnlyVideo,
        isExistingShare: EventUtil.isShared(eventProp),
        faceblur,
      });
    } else if (source === VIDEO_SOURCE_CLOUD) {
      const deviceId = camera.chId && siteCamerasById[camera.chId]?.deviceId;
      if (!deviceId) {
        console.error('No device ID found for camera', camera.id);
        return Promise.reject('No device ID found for camera');
      }
      return CloudVmsApi().startCloudVideoShare({
        tenantId: user.customerId,
        deviceId,
        eventId: clipEvent.id,
        payload: [
          {
            camId: camera.id,
            end,
            start,
            streamId: camera.streamId,
            metadata: { id: clipEvent.id, fillGaps, faceblur },
            share: true,
            merge: true,
          },
        ],
      });
    }
    return Promise.reject('The video is out of retention and cannot be added to the clip');
  };

  requestVmsPushToCloud({
    shareStartTime,
    shareEndTime,
    event,
  }: {
    shareStartTime: string;
    shareEndTime: string;
    event: IEvent;
  }) {
    const { locale, user, accessibleAddresses, event: prevEvent, siteCamerasById } = this.props;
    const { fillGaps, saveMotionOnlyVideo = false, camerasVideoSourceMap, faceblur } = this.state;

    if (!accessibleAddresses || Object.keys(accessibleAddresses).length === 0) {
      console.error('No accessible addresses found for site');
      return Promise.reject('No accessible addresses found for site');
    }
    if (!event) {
      console.error('No clip to attach the pushed video to');
      return Promise.reject('No clip to attach the pushed video to');
    }

    // Run up to 4 requests at a time
    const vmsRequestsQueue = new PQueue({ concurrency: 4 });
    const requestErrors: Record<string, any> = {};

    Object.values(camerasVideoSourceMap).forEach((camera) => {
      const {
        id: vmsCameraId,
        chId: cameraId,
        streamId,
        source,
        name: cameraName,
        streamName,
      } = camera;
      if (source) {
        // Get the camera's device info to find the correct accessible address
        const cameraDetails = cameraId ? siteCamerasById[cameraId] : undefined;
        const accessibleAddress = cameraDetails
          ? getAccessibleAddressForCamera(cameraDetails, accessibleAddresses)
          : undefined;

        if (!accessibleAddress) {
          console.error('Cannot find accessible address for camera device:', cameraId);
          requestErrors[vmsCameraId] = new Error(
            'Cannot find accessible address for camera device',
          );
          return;
        }
        const start = new Date(shareStartTime).getTime();
        const end = new Date(shareEndTime).getTime();

        const analyticsParams = {
          eventId: event.id,
          cameraId: vmsCameraId,
          cameraName,
          streamId,
          streamName,
          videoSource: source,
          startTime: shareStartTime,
          endTime: shareEndTime,
        };

        if (source === VIDEO_SOURCE_LOCAL) {
          vmsRequestsQueue.add(() =>
            SaveClipUtils.pushLocalVideo({
              start,
              end,
              event,
              camera,
              accessibleAddress,
              fillGaps,
              saveMotionOnlyVideo,
              isExistingShare: EventUtil.isShared(prevEvent),
              faceblur,
            })
              .then(() => {
                AnalyticsLogger.uploadClipVideo({ ...analyticsParams, isSuccess: true });
              })
              .catch((error) => {
                requestErrors[vmsCameraId] = error;
                AnalyticsLogger.uploadClipVideo({
                  ...analyticsParams,
                  isSuccess: false,
                  errorMessage: error.message,
                });
              }),
          );
        } else if (source === VIDEO_SOURCE_CLOUD) {
          const deviceId = camera.chId && siteCamerasById[camera.chId]?.deviceId;
          if (!deviceId) {
            console.error('No device ID found for camera', camera.id);
            return Promise.reject('No device ID found for camera');
          }
          /** due to the not customizable 30s timeout, each camera need a separate cloud request */
          const payload = [
            {
              camId: vmsCameraId,
              end,
              eventId: event.id,
              start,
              streamId,
              metadata: { id: event.id, fillGaps, faceblur },
              share: true,
              merge: true,
            },
          ];
          vmsRequestsQueue.add(() =>
            CloudVmsApi()
              .startCloudVideoShare({
                tenantId: user.customerId,
                deviceId,
                eventId: event.id,
                payload,
              })
              .then(() => AnalyticsLogger.uploadClipVideo({ ...analyticsParams, isSuccess: true }))
              .catch((error) => {
                requestErrors[vmsCameraId] = error;
                AnalyticsLogger.uploadClipVideo({
                  ...analyticsParams,
                  isSuccess: false,
                  errorMessage: error.message,
                });
              }),
          );
        }
      }
    });

    // Wait for all the requests to finish
    return vmsRequestsQueue
      .onIdle()
      .then(() => {
        if (Object.keys(requestErrors).length > 0) {
          // If there was any error, throw the first error
          throw Object.values(requestErrors)[0];
        }
        return Promise.resolve();
      })
      .catch((error) => {
        toast.error(locale.error.unableToPushVideoForClip, error);
        return Promise.reject(new Error('Cameras failed to push to cloud'));
      });
  }

  requestConnectDownload(event: IEvent) {
    const { locale, defaultVideoFormat, user, accessibleAddresses, siteCamerasById } = this.props;
    const { camerasVideoSourceMap } = this.state;
    const { downloads, id: eventId, startTime: eventStartTime, endTime: eventEndTime } = event;
    const startTime = new Date(eventStartTime).getTime();
    const endTime = new Date(eventEndTime).getTime();
    const customerName = user.profile.customerName ?? '';

    if (!downloads) {
      console.error('No downloads found for event');
      return Promise.reject('No downloads found for event');
    }

    const downloadStreamIds = downloads.map((download) => download.streamId);

    // Check that we have accessible addresses for the site
    if (!accessibleAddresses || Object.keys(accessibleAddresses).length === 0) {
      console.error('No accessible addresses found for site');
      return Promise.reject('No accessible addresses found for site');
    }

    const requests: any[] = [];
    const camerasByDevice: {
      [deviceId: DeviceId]: {
        connect: VmsApiType;
        cameras: Array<{
          cameraId: string;
          streamId: string;
          cameraName: string;
          notes: string;
        }>;
      };
    } = {};

    // First pass: group cameras by device and create VMS connections
    for (const camera of Object.values(camerasVideoSourceMap)) {
      const { id: cameraId, chId: chCameraId, streamId, name: cameraName } = camera;
      const notes = `${customerName}\n${this.locationName}\n${cameraName}`;

      if (streamId && downloadStreamIds.includes(streamId)) {
        // Get the camera's device info to find the correct accessible address
        const cameraDetails = chCameraId ? siteCamerasById[chCameraId] : undefined;
        const accessibleAddress = cameraDetails
          ? getAccessibleAddressForCamera(cameraDetails, accessibleAddresses)
          : undefined;

        if (!cameraDetails || !accessibleAddress) {
          console.error('Cannot find accessible address for camera device:', cameraId);
          continue;
        }

        // Group cameras by device
        if (!camerasByDevice[cameraDetails.deviceId]) {
          camerasByDevice[cameraDetails.deviceId] = {
            connect: VmsApi(accessibleAddress, false),
            cameras: [],
          };
        }
        camerasByDevice[cameraDetails.deviceId].cameras.push({
          cameraId,
          streamId,
          cameraName,
          notes,
        });
      }
    }

    // Second pass: create download requests for each device group
    for (const deviceGroup of Object.values(camerasByDevice)) {
      const { connect } = deviceGroup;

      for (const { cameraId, streamId, notes } of deviceGroup.cameras) {
        const request = new Promise((innerResolve) => {
          const alias = `${eventId.replace(/-/g, '_')}_${streamId.replace(/-/g, '_')}`;

          let downloadType = defaultVideoFormat;
          if (downloadType === 'mp4_subs') {
            // "mp4_subs" deprecated
            downloadType = 'mp4';
          } else if (!(VIDEO_DOWNLOAD_TYPES as readonly string[]).includes(downloadType)) {
            downloadType = DEFAULT_DOWNLOAD_TYPE;
          }

          connect
            .downloadVideo(cameraId, streamId, startTime, endTime, downloadType, alias, notes)
            .then(() => {
              return innerResolve({ streamId, success: true });
            })
            .catch((error: any) => {
              return innerResolve({
                streamId,
                success: false,
                message: error.apiMessage,
              });
            });
        });

        requests.push(request);
      }
    }

    return Promise.all(requests).then(
      (values) => {
        let hasError = false;
        let errorMessage: string | undefined;

        const downloadStreams = cloneDeep(this.state.downloadStreams);
        values.forEach((value) => {
          const { success, message, streamId } = value;
          if (!success) {
            if (!errorMessage && message?.startsWith('queue is full')) {
              errorMessage = locale.error.downloadQueueFull;
            }
            downloadStreams[streamId].state = CONNECT_DOWNLOAD_STATUS_FAILED;
            hasError = true;
          }
        });

        if (hasError) {
          const message = errorMessage || locale.error.connectDevice;
          toast.error(message, { autoClose: false });
          this.setState(
            {
              downloadStreams,
            },
            () => {
              this.downloadDebounce();
            },
          );

          return Promise.reject('No success');
        }
        return Promise.resolve();
      },
      (error) => Promise.reject(error),
    );
  }

  /** Update the thumbnail and thumbnailTypes of the selectedCameras state.
   *  This is used to make sure the thumbnails are updated from video snapshot to saved S3 image
   *  after the cameras info are saved to the event.
   *  This usually happens after user saved a new clip or added a new camera to an existing clip.
   */
  updateThumbnails = (selectedCameras: SavePanelCamerasById, eventCameras: EventCamera[]) => {
    const newSelectedCameras: SavePanelCamerasById = {};
    for (const eventCamera of eventCameras) {
      const { id, thumbnail } = eventCamera;
      const newSelectedCamera = cloneDeep(selectedCameras[id]);
      if (newSelectedCamera) {
        newSelectedCamera.thumbnailType = THUMBNAIL_TYPE_EVENT;
        newSelectedCamera.thumbnail = thumbnail;
        newSelectedCameras[id] = newSelectedCamera;
      } else {
        throw new Error('Camera not found in selectedCameras state');
      }
    }
    return newSelectedCameras;
  };

  handleUpdateShares = ({
    payload,
    message,
  }: {
    payload: Partial<IEvent>;
    message?: string;
  }): Promise<IEvent> => {
    const { locale } = this.props;
    const { originalEvent, selectedCameras } = this.state;

    if (!originalEvent) {
      return Promise.reject(new Error('No original event'));
    }

    return new Promise((resolve, reject) => {
      CloudVmsApi()
        .updateSharedEvent({ eventId: originalEvent.id }, payload)
        .then((updatedEvent) => {
          const { cameras } = updatedEvent;
          const newSelectedCameras = this.updateThumbnails(selectedCameras, cameras);
          const newState: Partial<SavePanelState> = {
            description: EventUtil.getDescription(updatedEvent),
            diffShares: [],
            isLoading: false,
            originalSelectedCameras: newSelectedCameras,
            originalEvent: updatedEvent,
            saveExistingEvent: false,
            saveMode: false,
            selectedCameras: newSelectedCameras,
            selfShare: updatedEvent.shares
              ? SaveClipUtils.getSelfShare(updatedEvent.shares as Share[])
              : {},
            shareMessage: EventUtil.getShareMessage(updatedEvent),
            shares: updatedEvent.shares ? (clone(updatedEvent.shares) as Share[]) : [],
            title: updatedEvent.title,
            visibility: SaveClipUtils.getClipVisibility(updatedEvent),
          };

          this.syncEvent(updatedEvent);

          if (message) {
            toast.success(message);
          }

          this.setState(newState as SavePanelState);
          return resolve(updatedEvent);
        })
        .catch((error: Error) => {
          toast.error(locale.error.platformFail);

          this.stopLoading();
          return reject(error);
        });
    });
  };

  handleUpdateEvent(
    payload: Partial<IEvent>,
    event = this.state.originalEvent,
    showSuccess = false,
    showFail = true,
  ): Promise<IEvent> {
    const { locale } = this.props;

    if (!event) {
      return Promise.reject(new Error('No event'));
    }

    const { details, id: eventId, scope, subtitle, title } = event;
    const description = details?.description;
    const favourite = details?.favourite;
    const favouritedBy = details?.favouritedBy;
    const visibility = scope?.visibility;

    const generalEventDetails = {
      details: {
        description,
        favourite,
        favouritedBy,
        downloads: payload.downloads,
      },
      scope: { visibility },
      subtitle,
      title,
    };

    const editedEvent = Object.assign(payload, generalEventDetails);
    const { selectedCameras } = this.state;

    // TODO analytics
    return new Promise((resolve, reject) => {
      EventActions.editEvent(eventId, editedEvent)
        .then((updatedEvent) => {
          const { cameras } = updatedEvent;
          const newSelectedCameras = this.updateThumbnails(selectedCameras, cameras);

          if (showSuccess) {
            toast.success(locale.messages.updateSuccess);
          }

          this.setState({
            description: EventUtil.getDescription(updatedEvent),
            isLoading: false,
            originalSelectedCameras: newSelectedCameras,
            originalEvent: updatedEvent,
            saveMode: false,
            selectedCameras: newSelectedCameras,
            title: updatedEvent.title,
            visibility: SaveClipUtils.getClipVisibility(updatedEvent) ?? VISIBILITY_PUBLIC,
          });

          return resolve(updatedEvent);
        })
        .catch(() => {
          if (showFail) {
            toast.error(locale.error.platformFail);
          }

          this.stopLoading();
          return reject();
        });
    });
  }

  stopLoading = () => this.setState({ isLoading: false });

  generateProgressName = (eventId: string, streamId: string | null = null) => {
    return isNil(streamId) ? `cloud_panel_${eventId}` : `cloud_panel_${eventId}_${streamId}`;
  };

  canCreateDownload = () => {
    return (
      FeaturesUtil.canDownload() &&
      (Object.values(this.props.areDevicesLocal).some(Boolean) || this.props.alwaysLocal)
    );
  };

  shouldDisableDownload = () => {
    const { errorTime, selectedCameras, isLoading, camerasVideoSourceMap } = this.state;
    const isAnyLocalVideoAvailable = Object.values(camerasVideoSourceMap).some(
      (camera) => camera.source === VIDEO_SOURCE_LOCAL,
    );
    return (
      isLoading ||
      !this._validateTitle() ||
      !!errorTime ||
      Object.keys(selectedCameras || {}).length === 0 ||
      !isAnyLocalVideoAvailable
    );
  };

  shouldDisableSubmit() {
    const {
      errorTime,
      ifShare,
      isCameraSelectorOpen,
      isLoading,
      longDuration,
      selectedCameras,
      camerasVideoSourceMap,
    } = this.state;

    const isAnyVideoStreamHd = Object.values(camerasVideoSourceMap).some(
      (camera) => camera.streamName === 'HD',
    );
    const noVideo = SaveClipUtils.noAvailableVideoForClip(camerasVideoSourceMap);

    let submitDisabled =
      isLoading ||
      !this._validateTitle() ||
      (ifShare && !this._validateEmail()) ||
      !!errorTime ||
      Object.keys(selectedCameras || {}).length === 0 ||
      (this.isClipDurationRestricted &&
        longDuration &&
        (isAnyVideoStreamHd || KioskUtils.isKiosk())) ||
      isCameraSelectorOpen ||
      noVideo ||
      this.hasExceededFaceblurLimit();

    if (!submitDisabled) {
      const {
        title,
        originalEvent,
        description,
        contextMode,
        diffShares,
        shareMessage,
        startTime,
        endTime,
      } = this.state;

      // Implemented like this for readability
      const hasChanges =
        title !== originalEvent?.title ||
        description !== originalEvent?.details?.description ||
        (contextMode === CONTEXT_SAVETOCLOUD &&
          (!isEmpty(diffShares) || shareMessage !== originalEvent.details?.shareMessage)) ||
        (contextMode !== CONTEXT_DOWNLOAD &&
          ((startTime && !DateHandler.isSame(originalEvent.startTime, startTime)) ||
            (endTime && !DateHandler.isSame(originalEvent.endTime, endTime)))) ||
        keys(selectedCameras).length !== originalEvent.cameras.length;

      submitDisabled = !hasChanges;
    }

    return submitDisabled;
  }

  handleSaveComplete = () => {
    const { locale } = this.props;
    const { originalEvent } = this.state;
    if (!originalEvent) {
      throw new Error('Clip event is not defined');
    }
    toast.success(locale.pushToCloudState.done);
    this.setState(
      {
        displaySaveAsInProgress: false,
        pollingState: CLOUD_STATUS_COMPLETE,
        uploadingPercentage: null,
      },
      () => {
        const isComplete = true;
        this.loadLatestEvent(originalEvent, isComplete);
      },
    );
  };

  handlePollCloudChange = () => {
    const { originalEvent, displaySaveAsInProgress } = this.state;
    if (!originalEvent) {
      throw new Error('Clip event is not defined');
    }
    const { id } = originalEvent;
    const eventProgress = PollCloudStore.getEventProgress(id);
    const pollingState = PollCloudStore.getPollingState(originalEvent);

    this.setState({
      readOnly: PollCloudStore.isInProgress(originalEvent) || displaySaveAsInProgress,
      pollingState,
      uploadingPercentage: eventProgress,
    });
  };

  handlePollConnectChange = () => {
    const { downloadStreams, originalEvent, selectedCameras } = this.state;
    const { user, areDevicesLocal, accessibleAddresses } = this.props;
    const newDownloadStreams = cloneDeep(downloadStreams);

    Object.keys(newDownloadStreams).forEach((streamId) => {
      const downloadStream = newDownloadStreams[streamId];
      const properties = PollConnectStore.getDownloadProperties(originalEvent, streamId);

      // Check if state transitioned to ready
      if (!downloadStream.toReady) {
        downloadStream.toReady =
          (downloadStream.state === CONNECT_DOWNLOAD_STATUS_ENQUEUED ||
            downloadStream.state === CONNECT_DOWNLOAD_STATUS_PROCESSING) &&
          properties.state === CONNECT_DOWNLOAD_STATUS_READY;
      }

      downloadStream.state = properties.state;
      downloadStream.expiration =
        properties.state === CONNECT_DOWNLOAD_STATUS_EXPIRED
          ? downloadStream.expiration
          : properties.expiration;
      downloadStream.downloadCount =
        downloadStream.downloadCount == null || downloadStream.downloadCount < properties.count
          ? properties.count
          : downloadStream.downloadCount;
      downloadStream.percent = properties.percent;

      let camera: SavePanelCamera | undefined;
      for (const cameraId of Object.keys(selectedCameras)) {
        const selectedCamera = selectedCameras[cameraId];
        if (selectedCamera.streams.find((stream) => stream.id === streamId)) {
          camera = selectedCamera;
          break;
        }
      }
      const shouldAutoDownload =
        !!downloadStream.toReady &&
        (!downloadStream.downloaded ||
          !Object.keys(downloadStream.downloaded).length ||
          downloadStream.downloadCount === 0) &&
        camera &&
        !!areDevicesLocal[camera.deviceId];

      if (shouldAutoDownload) {
        const downloadedOn = DateHandler.getTimestamp();
        const email = user.jwtUser.email;
        if (downloadStream?.downloaded?.[email]) {
          downloadStream.downloaded[email].push(downloadedOn);
        } else {
          set(downloadStream, ['downloaded', email], [downloadedOn]);
        }

        downloadStream.state = CONNECT_DOWNLOAD_STATUS_DOWNLOADED;
        downloadStream.downloadCount++;
        downloadStream.toReady = false;

        // PollConnectStore.getDownloadProperties() does not return a deviceId,
        // so we look up the AuthLocation from the camera in scope (mirroring
        // the construction in DownloadDetails.tsx).
        const deviceId = camera!.deviceId;
        const fullDevice = this.location.devices?.find((d) => d.id === deviceId);
        const device: AuthLocation = {
          id: deviceId,
          name: fullDevice?.name ?? '',
          accessibleAddress: accessibleAddresses[deviceId] || '',
        };

        // Don't download all files at once to avoid warnings (this is supposed to also allow it to work in IE)
        setTimeout(() => {
          const { fileName, link, sizeMB } = properties;
          this._simulateDownloadLinkClick(link, device, { fileName, sizeMB });
        }, AUTO_DOWNLOAD_TIMEOUT_MS);
      }
    });

    this.setState(
      {
        downloadStreams: newDownloadStreams,
      },
      () => {
        if (!PollConnectStore.isProcessing(originalEvent)) {
          this.downloadDebounce();
        }
      },
    );
  };

  _handleDownloadUpdate() {
    const { originalEvent, downloadStreams: oldDownloadStreams } = this.state;
    const { user } = this.props;
    const statesToSave = [CONNECT_DOWNLOAD_STATUS_CANCELLED, CONNECT_DOWNLOAD_STATUS_FAILED];
    const downloads: {
      streamId: string;
      expiration?: number;
      downloaded?: { email: string; downloadedAt: number[] }[];
      state?: DownloadStream['state'];
    }[] = [];
    const email = user.jwtUser.email;
    if (!originalEvent) {
      throw new Error('Clip event is not defined');
    }
    const prevDownloadStreams = this._parseDownloadStreams(originalEvent);
    const downloadStreams = cloneDeep(oldDownloadStreams);

    Object.keys(downloadStreams).forEach((streamId) => {
      const downloadStream = downloadStreams[streamId];

      // Check if download needs to be updated (only for currently signed-in user)
      let downloadedAt;
      if (downloadStream.downloaded?.[email]) {
        downloadedAt = difference(
          downloadStream.downloaded[email],
          prevDownloadStreams?.[streamId]?.downloaded?.[email] || [],
        );
      }

      // Check if expiration needs to be updated
      let expiration;
      const prevExpiration = prevDownloadStreams[streamId]?.expiration ?? undefined;
      const currentExpiration = downloadStream.expiration ?? undefined;
      if (!isNil(currentExpiration) && prevExpiration !== currentExpiration) {
        // Update expiration
        expiration = currentExpiration;
      } else if (
        downloadStream.state === CONNECT_DOWNLOAD_STATUS_EXPIRED &&
        prevExpiration === undefined
      ) {
        // Edge case where download was never viewed after being initiated and enough time has passed that it is no longer stored in Connect's database
        expiration = DateHandler.getTimestamp();
      }

      let downloadState;
      if (
        statesToSave.includes(downloadStream.state) &&
        downloadStream.state !== prevDownloadStreams[streamId].state
      ) {
        downloadState = downloadStream.state;
      }

      if (downloadedAt || expiration || downloadState) {
        const downloadDetails: {
          streamId: string;
          expiration?: number;
          downloaded?: { email: string; downloadedAt: number[] }[];
          state?: DownloadStream['state'];
        } = {
          streamId,
        };

        if (downloadedAt) {
          set(downloadDetails, ['downloaded', 0], {
            email,
            downloadedAt,
          });
        }

        if (expiration) {
          downloadDetails.expiration = expiration;
        }

        if (downloadState) {
          downloadDetails.state = downloadState;
        }

        downloads.push(downloadDetails);
      }
    });

    if (downloads.length > 0) {
      const payload = {
        downloads,
      };

      this.handleUpdateEvent(payload as Partial<IEvent>);
    }
  }

  _simulateDownloadLinkClick = (
    link: string,
    device: AuthLocation,
    metadata: { fileName?: string; sizeMB?: number } = {},
  ) => {
    const { locale, accessibleAddresses } = this.props;
    const { originalEvent } = this.state;
    const accessibleAddress = accessibleAddresses[device.id as DeviceId];
    const quickConnectURL = `${accessibleAddress}/id`;
    const event = originalEvent || this.props.event;
    if (!event) {
      throw new Error('Clip event is not defined');
    }

    if (accessibleAddress === undefined) {
      throw new Error('No accessible address available');
    }

    // TODO: Note that we're fetching '/id' here, but this URL isn't protected
    // by any authentication, so this will always succeed.
    const fetchParams = AuthService.getLocalFetchParams(
      // TODO: update type of function getLocalFetchParams param's location in vd-common
      { ...device, accessibleAddress },
      quickConnectURL,
    );
    fetch(...fetchParams)
      .then(() => {
        // Add temporary link
        const tmpAnchor = document.createElement('a');
        tmpAnchor.href = link;
        tmpAnchor.target = '_parent';
        tmpAnchor.download = '';
        (document.body || document.documentElement).appendChild(tmpAnchor);

        // Simulate link click
        tmpAnchor.click();

        // Delete temporary link
        tmpAnchor?.parentNode?.removeChild(tmpAnchor);

        const { endTime, startTime } = event;
        const { fileName, sizeMB } = metadata;
        AnalyticsLogger.downloadVideo({
          event,
          durationInSec: (new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000,
          fileName,
          fileSizeMB: sizeMB,
        });
      })
      .catch((error) => {
        console.error('Talk to VMS before download failed.', error);
        toast.error(locale.error.connectDevice);
      });
  };

  onDownloadDetailRowButtonClick = (
    link: string,
    device: AuthLocation,
    streamId: string,
    metadata = {},
  ) => {
    this._simulateDownloadLinkClick(link, device, metadata);

    const downloadStreams = cloneDeep(this.state.downloadStreams);
    if (
      has(downloadStreams, [streamId, 'downloadCount']) &&
      downloadStreams[streamId].downloadCount >= 0
    ) {
      downloadStreams[streamId].downloadCount++;
    } else {
      downloadStreams[streamId].downloadCount = 1;
    }

    const downloadedOn = DateHandler.getTimestamp();
    const { user } = this.props;
    const email = user.jwtUser.email;

    if (has(downloadStreams, [streamId, 'downloaded', email])) {
      downloadStreams[streamId]['downloaded'][email].push(downloadedOn);
    } else {
      set(downloadStreams, [streamId, 'downloaded', email], [downloadedOn]);
    }

    this.setState(
      {
        downloadStreams,
      },
      () => {
        this.downloadDebounce();
      },
    );
  };

  _parseDownloadStreams(event: IEvent) {
    const downloadStreams: {
      [key: string]: {
        expiration?: number | undefined;
        downloaded: {
          [key: string]: any;
        };
        downloadCount?: number;
        state?: ConnectDownloadStatus;
      };
    } = {};
    if (!event.downloads) {
      throw new Error('Event downloads are not defined');
    }
    event.downloads.forEach((download) => {
      const streamId = download.streamId;

      downloadStreams[streamId] = {
        expiration: download.expiration,
        downloaded: {},
      };

      let count = 0;
      download.downloaded?.forEach((details) => {
        downloadStreams[streamId].downloaded[details.email] = cloneDeep(details.downloadedAt);
        count += details.downloadedAt.length;
      });

      downloadStreams[streamId].downloadCount = count;
    });

    return downloadStreams;
  }

  enterEditMode = () => this.setState({ saveMode: true });

  exitEditMode = () => {
    const { originalEvent, originalSelectedCameras } = this.state;
    if (!originalEvent) {
      throw new Error('Clip event is not defined');
    }
    const { title, shares, startTime, endTime } = originalEvent;
    // when user canceled editting, everything should be reset to the status before the edit.
    this.setState({
      emails: '',
      errorEmail: '',
      title,
      description: EventUtil.getDescription(originalEvent),
      ifShare: this.checkIfShare(originalEvent),
      isCameraSelectorOpen: false,
      shares: shares ? (cloneDeep(shares) as Share[]) : [],
      diffShares: [],
      shareMessage: EventUtil.getShareMessage(originalEvent),
      saveMode: false,
      selectedCameras: cloneDeep(originalSelectedCameras),
      startTime,
      endTime,
      longDuration: this.isLongDuration(startTime, endTime),
      shortDuration: this.isShortDuration(startTime, endTime),
      visibility: SaveClipUtils.getClipVisibility(originalEvent) ?? VISIBILITY_PUBLIC,
    });
  };

  updateSharesState = (shares: Share[]) => this.setState({ shares });

  updateDiffSharesState = (diffShares: Partial<Share>[]) => this.setState({ diffShares });

  updateEmailsState = (emails: string) => this.setState({ emails });

  updateShareMessageState = (shareMessage: string) => this.setState({ shareMessage });

  onFocusShareMessage = () => this.setState({ showCharCountShareMessage: true });

  checkIfShare = (event: IEvent) => event.shares && event.shares.length > 1;

  viewSharePage = () => {
    const {
      originalEvent: event,
      selfShare: { id, sharedBy, url },
    } = this.state;

    window.open(url, '_blank');
    AnalyticsLogger.clickViewSharePage({ event, shareId: id, sharedBy });
  };

  _requestSharedViewLink(eventId: string, shares: Share[], untilSuccess = false) {
    if (KioskUtils.isKiosk()) {
      return;
    }

    const selfShare = SaveClipUtils.getSelfShare(shares);

    if (selfShare?.id) {
      CloudVmsApi()
        .getSharedViewLink({ eventId, shareId: selfShare.id })
        .then((link: any) => {
          const { token, url } = link;

          this.getShareLinkDelay = INITIAL_ATTEMPT_DELAY;
          this.setState({
            selfShare: {
              ...selfShare,
              token,
              url,
            },
            selfSharePolling: false,
          });
        })
        .catch((error: Error) => {
          console.warn(`Error getting shared view link: ${error}`);

          const { displaySaveAsInProgress } = this.state;

          if (!displaySaveAsInProgress) {
            this.setState({
              displaySaveAsInProgress: true,
            });
          }

          if (untilSuccess) {
            if (this.getShareLinkDelay < MAX_ATTEMPT_DELAY) {
              this.getShareLinkDelay += 1000;
            }

            setTimeout(() => {
              this._requestSharedViewLink(eventId, shares, untilSuccess);
            }, this.getShareLinkDelay);
          }
        });
    }
  }

  getFaceblurClipDuration = () => {
    const { startTime, endTime, faceblur, selectedCameras } = this.state;
    const shareDuration = new Date(endTime as any).getTime() - new Date(startTime as any).getTime();
    const blurDurationInMs = faceblur ? shareDuration * Object.keys(selectedCameras).length : 0;
    return blurDurationInMs;
  };

  getNewFaceblurQuota = () => {
    const { faceblurQuota } = this.props;
    const remainingSeconds = faceblurQuota.data?.usage.seconds.remaining ?? 0;
    const blurredLimit = remainingSeconds * 1000;
    const blurDurationInMs = this.getFaceblurClipDuration();
    return blurredLimit - blurDurationInMs;
  };

  hasExceededFaceblurLimit = () => {
    const { faceblur } = this.state;
    return faceblur && this.getNewFaceblurQuota() < 0;
  };
}

const ComposedSavePanel = (props: SavePanelProps) => {
  const {
    playingCameras,
    preferences: { defaultVideoFormat },
    event,
  } = props;
  const locale = useLocale();
  const { height } = useWindowSize();
  const user = useUserSession();

  const { devicesById } = useDevices();
  const { sitesById } = useSites();

  const primaryDeviceId = event ? event.locationId : playingCameras[0]?.camera.deviceId;
  const siteId = event ? event.siteId : playingCameras[0]?.camera.siteId;
  const site = siteId && sitesById?.[siteId];

  const faceblurQuota = useFaceBlurQuota(primaryDeviceId || '');

  const {
    authData: { useLocalIpAsPrimary },
  } = useContext(CommonContext);
  const { syncTimelineEvent } = useContext(VideoContext);

  const { cameras: siteCameras, camerasById: siteCamerasById } = useCamerasFromSites(
    siteId ? [siteId] : [],
  );

  const deviceIds = useMemo(() => site?.devices?.map((device) => device.id) ?? [], [site?.devices]);
  const areDevicesLocal = useDevicesAreLocal(deviceIds);

  // Build accessibleAddresses map for all devices at this site
  const accessibleAddresses = useMemo(() => {
    const addressesMap: AccessibleAddresses = {};

    for (const deviceId of deviceIds) {
      const device = devicesById[deviceId];
      if (device) {
        const isLocal = areDevicesLocal[deviceId];
        const accessibleAddress = getAccessibleAddress(device, isLocal);
        if (accessibleAddress) {
          addressesMap[deviceId] = accessibleAddress;
        }
      }
    }
    return addressesMap;
  }, [devicesById, deviceIds, areDevicesLocal]);

  if (!site) {
    throw new Error('Location not found');
  }

  if (
    Object.values(areDevicesLocal).every((value) => value === undefined) ||
    siteCameras.length === 0 ||
    Object.keys(siteCamerasById).length === 0
  ) {
    return <CenteredLoadingSpinner />;
  }

  return (
    <SavePanel
      locale={locale}
      location={site}
      areDevicesLocal={areDevicesLocal}
      alwaysLocal={useLocalIpAsPrimary}
      syncTimelineEvent={syncTimelineEvent}
      windowHeight={height}
      user={user}
      defaultVideoFormat={defaultVideoFormat}
      faceblurQuota={faceblurQuota}
      accessibleAddresses={accessibleAddresses}
      cameras={siteCameras}
      siteCamerasById={siteCamerasById}
      {...props}
    />
  );
};

export type GetPushToCloudStatusForOneCam = InstanceType<
  typeof SavePanel
>['getPushToCloudStatusForOneCam'];

export default ComposedSavePanel;

function numberToDate(date: Date | number | undefined): Date | undefined {
  if (date === undefined || date === null) {
    return date;
  } else if (typeof date === 'number') {
    return new Date(date);
  }
  return date;
}
